import { CollectionTableColumn } from "~/constants/collections"
import { NoosOrderSuggestionsColumn, SupplyTableColumn } from "~/constants/supplyPlan"
import { TableColumnGroups } from "~/constants/tableColumnGroups"
import { convertPercentileToPercentLabel } from "~/utils/abcUtils"
import { SalesMetricColumns, TrimMetricsColumns } from "~/utils/demandDictionary"
import { formatSecondaryMOQName } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import {
  getAtRiskDescriptionShort,
  getUnfulfillableDescriptionShort,
} from "~/utils/unfulfillablePreorderUtils"
import { ProductTableColumn } from "~/utils/productTableUtils"
import { SIZE_DIMENSION_1_LABEL, SIZE_DIMENSION_2_LABEL } from "~/utils/sizeDimensions"

// Type-only imports of every other per-table column enum, unioned into `ColumnNamingId` below.
// These are `import type` so they're erased at build time and never create a runtime import cycle.
import type { Column as CollectionManagementColumn } from "~/components/collections/CollectionManagementList"
import type { MultipleCollectionsTableColumn } from "~/components/collections/MultipleCollectionsTable"
import type { BudgetSalesPricesColumn } from "~/components/budget/BudgetSalesPricesView"
import type { FashionSupplyPlanColumn } from "~/components/fashion-supply-plan/FashionSupplyPlanList"
import type { BuysListColumn } from "~/components/noos-supply-plan/BuysList"
import type { SupplyPlanListColumn } from "~/components/noos-supply-plan/NoosSupplyPlanList"
import type { PurchaseOrderListColumn } from "~/components/purchaseOrder/tabs/purchaseOrderListColumns"
import type { PurchasePlanListColumn } from "~/components/purchase-plan/PurchasePlanList"
import type { CustomerTableColumn } from "~/components/reusable/CustomerTable"
/* eslint-disable no-restricted-syntax -- type-only imports for the column naming
 registry; erased at compile time, so the prototype isolation boundary holds. */
import type { Demand2NpiColumn } from "~/components/demand-2/demand/NewVariantsView"
import type { Demand2BaselineColumn } from "~/components/demand-2/demand/baselineColumnIds"
import type { Demand2PreorderColumn } from "~/components/demand-2/demand/PreordersView"
import type { Demand2CarryOverColumn } from "~/components/demand-2/shared/CarryOverView"
import type { Demand2PlanPeriodColumn } from "~/components/demand-2/shared/planPeriodColumns"
import type { Demand2LifecycleStageColumn } from "~/components/demand-2/shared/lifecycleStageColumnId"
import {
  Demand2SignalColumn,
  type Demand2TotalColumn,
  signalColumnId,
} from "~/components/demand-2/demand/totalColumnIds"
import { insightLabelFor } from "~/components/demand-2/shared/noosBreakdown"
import { INSIGHT_KEY_ORDER, isStrategicInsightKey } from "~/utils/demandInsightValues"
import type { Demand2PublishedLeafColumn } from "~/components/demand-2/tabs/PerformanceTab"
/* eslint-enable no-restricted-syntax */
import type { VendorCapacityColumn } from "~/components/vendor-management/VendorCapacityTable"
import type { BuyTableColumn } from "~/constants/buyTable"
import { BuySpecificColumn } from "~/constants/buyTable"
import type { CommonColumn } from "~/constants/commonColumns"
import {
  FORECAST_QUALITY_THRESHOLDS,
  ForecastEvaluationColumn,
} from "~/constants/forecastEvaluation"
import type { IncomingOrdersOverviewTableColumn } from "~/constants/incomingOrders"
import { NpiListColumns } from "~/constants/nearestNeighbors"
import { PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"
import { ReplenishmentTableColumn } from "~/constants/replenishmentTable"
import { AllocationStoreColumn } from "~/constants/allocationStoreTable"
import { STORE_FIELD_LABELS } from "~/utils/allocationStores"
import type { NpiTableColumn } from "~/utils/productTableUtils"
import type { VendorManagementColumn } from "~/routes/_auth/procurement/vendor-management"

export { TableColumnGroups }

export enum BudgetVariantColumn {
  BudgetForecast = "budgetForecast",
  ForecastedRevenue = "forecastedRevenue",
  AllocatedBudgetUnits = "allocatedBudgetUnits",
  AllocatedBudgetCurrency = "allocatedBudgetCurrency",
  VarianceUnits = "varianceUnits",
  VarianceCurrency = "varianceCurrency",
}

export enum AdjustmentManagerColumn {
  Theme = "theme",
  Warehouse = "warehouse",
  Channels = "channels",
  Value = "value",
  VariantCount = "variantCount",
  Period = "period",
  UpdatedBy = "updatedBy",
}

export enum NpiDialogMatchColumn {
  MatchScore = "matchScore",
  DemandBasis = "demandBasis",
  BasisSales = "basisSales",
}

export enum DemandNpiMatchColumn {
  Collections = "collections",
  CollectionSales = "collectionSales",
  NoosSales = "noosSales",
  NoosLastYearSales = "noosLastYearSales",
  MatchScore = "matchScore",
}

export enum MarketStrategyColumn {
  Channel = "marketStrategyChannel",
  ChannelType = "marketStrategyChannelType",
  WhereItSells = "marketStrategyWhereItSells",
  Sales = "marketStrategySales",
}

/**
 * Columns whose naming is centralized in {@link columnNaming} — labels, tooltips and search
 * keywords for these live in one place. This is the key type of the `columnNaming` record.
 */
export type NamedColumnId =
  | ProductTableColumn
  | CollectionTableColumn
  | SalesMetricColumns
  | TrimMetricsColumns
  | SupplyTableColumn
  | NoosOrderSuggestionsColumn
  | PurchaseOrderTableColumn
  | BudgetVariantColumn
  | AdjustmentManagerColumn
  | NpiDialogMatchColumn
  | DemandNpiMatchColumn
  | MarketStrategyColumn
  | ForecastEvaluationColumn
  | BuySpecificColumn
  | NpiListColumns
  | ReplenishmentTableColumn
  | AllocationStoreColumn

/**
 * Every column id used anywhere in a Hakio table — the `colId` accepted by the shared column
 * factories ({@link import("~/utils/columnTemplates")}). It is the union of {@link NamedColumnId}
 * (which additionally has centralized naming) and every other per-table column enum, so a column
 * can't be created with an unregistered ad-hoc string id.
 */
export type ColumnNamingId =
  | NamedColumnId
  | NpiTableColumn
  | PurchaseOrderListColumn
  | BuyTableColumn
  | BuysListColumn
  | IncomingOrdersOverviewTableColumn
  | CommonColumn
  | MultipleCollectionsTableColumn
  | CollectionManagementColumn
  | PurchasePlanListColumn
  | SupplyPlanListColumn
  | FashionSupplyPlanColumn
  | VendorCapacityColumn
  | VendorManagementColumn
  | BudgetSalesPricesColumn
  | CustomerTableColumn
  | Demand2NpiColumn
  | Demand2BaselineColumn
  | Demand2PreorderColumn
  | Demand2CarryOverColumn
  | Demand2PlanPeriodColumn
  | Demand2LifecycleStageColumn
  | Demand2TotalColumn
  | Demand2PublishedLeafColumn

/**
 * Escape type for genuinely dynamic column ids (per-warehouse, calendar columns, …) that can't
 * be enum members. Cast such ids `as DynamicColumnId` — it's an alias of {@link ColumnNamingId},
 * so the cast satisfies `colId` while still signalling "this id is intentionally not registered".
 */
export type DynamicColumnId = ColumnNamingId

export interface ColumnNaming {
  id: NamedColumnId
  /** Header label shown in the table itself (was `headerName` in ProductTableV2) */
  columnTitle: string
  /** Secondary text shown under the header (was `headerSubtitle` at column creation) */
  columnSubtitle?: string
  /** Label shown in the column-chooser dialog (was `displayName` in getTableColumns) */
  columnChooserTitle: string
  /** Header / column-chooser tooltip (was `headerTooltip` / `description`) */
  tooltip?: string
  keyWords?: string[]
  group?: TableColumnGroups
}

// `formatSecondaryMOQName` reads from `sessionStorage`, which is unavailable during
// SSR / tests. Guard so this module-level record can be safely imported anywhere.
const secondaryMoqName =
  typeof sessionStorage !== "undefined" ? formatSecondaryMOQName() : "Secondary MOQ"

// The unconstrained suggestion and its MOQ-rounded sibling are shown on the NOOS Supply Plan
// overview, in the buy plan and on the purchase-order tables. They are the same figure everywhere,
// so the label and the explanation are declared once and reused by every registry (SLIM-575).
const SUGGESTED_ORDER_TITLE = "Suggested order"
const SUGGESTED_ORDER_TOOLTIP =
  "The unconstrained order quantity: your optimal quantity based on demand and safety stock, before any MOQ constraint is applied. Ordering this minimises stock liability."
// SUP-50: the buy plans' delta columns. The suggestion is recalculated on every load, so the delta
// is a live comparison, never a saved baseline.
const SUGGESTED_ORDER_DELTA_TITLE = "Delta"
const SUGGESTED_ORDER_DELTA_SUBTITLE = "vs suggested order"
const SUGGESTED_ORDER_DELTA_CHOOSER_TITLE = "Delta vs suggested order"
const suggestedOrderDeltaTooltip = (quantityName: string) =>
  `${quantityName} minus the suggested order. Positive means you buy more than suggested. The suggested order is recalculated each time the plan loads from current demand, stock and buffer settings, so the delta can change without an edit.`
const SUGGESTED_ORDER_DELTA_KEYWORDS = [
  "delta",
  "difference",
  "change",
  "suggested order",
  "variance",
]
const MOQ_SUGGESTED_ORDER_TITLE = "MOQ-Based Suggestion"
const MOQ_SUGGESTED_ORDER_TOOLTIP =
  "The suggested order raised to meet your supplier's minimum order quantity, which eliminates production surcharges. Ordering this may tie up more capital and warehouse capacity than strictly needed."

export const productColumnNaming: Record<ProductTableColumn, ColumnNaming> = {
  [ProductTableColumn.Adjustments]: {
    id: ProductTableColumn.Adjustments,
    columnTitle: "Adjustments",
    columnChooserTitle: "Adjustments",
    tooltip: "Adjustments made to the demand.",
    keyWords: [
      "adjustments",
      "adjustment",
      "adjustment count",
      "adjustment number",
      "demand adjustments",
      "forecast adjustments",
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ProductName]: {
    id: ProductTableColumn.ProductName,
    columnTitle: "Product Name",
    columnChooserTitle: "Product Name",
    tooltip: "Name of the product the variant belongs to.",
    keyWords: ["title", "label", "style", "product name"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Product]: {
    id: ProductTableColumn.Product,
    columnTitle: "Product",
    columnChooserTitle: "Product",
    tooltip: "Name of the product. A product groups its variants, such as the colors of one style.",
    keyWords: ["product", "product name", "product id", "style", "item"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Variant]: {
    id: ProductTableColumn.Variant,
    columnTitle: "Variant",
    columnChooserTitle: "Variant",
    tooltip: "Product variations such as different colors (often called style options).",
    keyWords: [
      "type",
      "model",
      "version",
      "style option",
      "variant",
      "item",
      "item no",
      "item number",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.ProductId]: {
    id: ProductTableColumn.ProductId,
    columnTitle: "Product ID",
    columnChooserTitle: "Product ID",
    tooltip: "The product's ID in your own systems. One product has one or more variants.",
    keyWords: ["product id", "product identifier", "item", "style"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.VariantId]: {
    id: ProductTableColumn.VariantId,
    columnTitle: "Variant ID",
    columnChooserTitle: "Variant ID",
    tooltip:
      "The variant's ID in your own systems. A variant is usually one color of a product, with one SKU per size.",
    keyWords: ["variant id", "variant identifier", "style", "item"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.VariantName]: {
    id: ProductTableColumn.VariantName,
    columnTitle: "Variant Name",
    columnChooserTitle: "Variant Name",
    tooltip:
      "The variant's name from your product data. Shows the color when the variant has no name.",
    keyWords: ["variant name", "variant", "style", "item"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SKU]: {
    id: ProductTableColumn.SKU,
    columnTitle: "SKU",
    columnChooserTitle: "SKU",
    tooltip: "The stock-keeping unit (SKU) used as a unique identifier in the system.",
    keyWords: ["product code", "sku"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Flags]: {
    id: ProductTableColumn.Flags,
    columnTitle: "Flags",
    columnChooserTitle: "Flags",
    tooltip: "Various flags associated with the product",
    keyWords: ["attributes", "markers", "indicators", "flags"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Rules]: {
    id: ProductTableColumn.Rules,
    columnTitle: "Rules",
    columnChooserTitle: "Rules",
    tooltip: "The rules this variant matches. Rules are set up in Settings › Rules.",
    keyWords: ["alerts", "rules", "forecast alerts", "flags", "warnings"],
    group: TableColumnGroups.ModelsAndAnalysis,
  },
  [ProductTableColumn.Brand]: {
    id: ProductTableColumn.Brand,
    columnTitle: "Brand",
    columnChooserTitle: "Brand",
    tooltip: "The brand the product is sold under, from your product data.",
    keyWords: ["manufacturer", "make", "brand"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Planner]: {
    id: ProductTableColumn.Planner,
    columnTitle: formatName("planner", false, true),
    columnChooserTitle: formatName("planner", false, true),
    tooltip: `The ${formatName("planner")} assigned to the product.`,
    keyWords: [
      "planner",
      "user",
      "assigned to",
      "assignee",
      "responsible",
      formatName("planner"),
      formatName("planner", true),
    ],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Category]: {
    id: ProductTableColumn.Category,
    columnTitle: "Category",
    columnChooserTitle: "Category",
    tooltip: "The product category from your product data.",
    keyWords: ["classification", "group", "type", "category", "categories", "product group"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Gender]: {
    id: ProductTableColumn.Gender,
    columnTitle: "Gender",
    columnChooserTitle: "Gender",
    tooltip: "The gender the product is made for, from your product data.",
    keyWords: ["suitability", "target gender", "sex", "gender"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Tags]: {
    id: ProductTableColumn.Tags,
    columnTitle: "Tags",
    columnChooserTitle: "Tags",
    tooltip: "Tags associated with the product",
    keyWords: ["keywords", "labels", "identifiers", "tags"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Color]: {
    id: ProductTableColumn.Color,
    columnTitle: "Color",
    columnChooserTitle: "Color",
    tooltip: "The variant's color as named in your product data.",
    keyWords: ["colour", "hue", "shade", "color"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.BaseColor]: {
    id: ProductTableColumn.BaseColor,
    columnTitle: "Base Color",
    columnChooserTitle: "Base Color",
    tooltip:
      "The standard color family Hakio maps the variant's color to, so similar shades group together.",
    keyWords: [
      "hue",
      "shade",
      "color",
      "base color",
      "normalized color",
      "colour",
      "base colour",
      "normalized colour",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Size]: {
    id: ProductTableColumn.Size,
    columnTitle: "Size",
    columnChooserTitle: "Size",
    tooltip: "The SKU's size, from your product data.",
    keyWords: ["dimension", "measurement", "proportion", "size"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SizeDimension1]: {
    id: ProductTableColumn.SizeDimension1,
    columnTitle: SIZE_DIMENSION_1_LABEL,
    columnChooserTitle: SIZE_DIMENSION_1_LABEL,
    tooltip:
      "The first part of a two-part size: the waist of jeans, the band of a bra or the width of bedding. Comes from your product data.",
    keyWords: ["waist", "width", "band", "dimension", "two-part size", "size"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SizeDimension2]: {
    id: ProductTableColumn.SizeDimension2,
    columnTitle: SIZE_DIMENSION_2_LABEL,
    columnChooserTitle: SIZE_DIMENSION_2_LABEL,
    tooltip:
      "The second part of a two-part size: the length of jeans, the cup of a bra or the length of bedding. Comes from your product data.",
    keyWords: ["length", "inseam", "cup", "dimension", "two-part size", "size"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SizeSplitConfidence]: {
    id: ProductTableColumn.SizeSplitConfidence,
    columnTitle: "Size Split Confidence",
    columnChooserTitle: "Size Split Confidence",
    tooltip: "Confidence level of the size split prediction based on historical data.",
    keyWords: ["size split", "confidence", "split confidence", "size distribution"],
    group: TableColumnGroups.ModelsAndAnalysis,
  },
  [ProductTableColumn.Fabric]: {
    id: ProductTableColumn.Fabric,
    columnTitle: "Fabric",
    columnChooserTitle: "Fabric",
    tooltip: "The fabric or material of the product, from your product data.",
    keyWords: ["fabric", "material", "fabric type", "fabric composition", "greige"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.AvailabilityToday]: {
    id: ProductTableColumn.AvailabilityToday,
    columnTitle: "Availability",
    columnChooserTitle: "Availability (Today)",
    columnSubtitle: "Today",
    tooltip: `The percentage of SKUs currently available for ${formatName("sale")}, weighted by expected demand.`,
    keyWords: ["current availability", "in stock today", "today's status", "availability today"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.OverstockQuantity]: {
    id: ProductTableColumn.OverstockQuantity,
    columnTitle: "Overstock quantity",
    columnChooserTitle: "Overstock quantity",
    tooltip:
      "The quantity of stock that exceeds the overstock threshold. The threshold can be adjusted in the settings.",
    keyWords: ["excess stock", "surplus quantity", "overstock amount", "overstock quantity"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.RightMargin]: {
    id: ProductTableColumn.RightMargin,
    columnTitle: "Right Margin",
    columnChooserTitle: "Right Margin",
    group: TableColumnGroups.Other,
  },
  [ProductTableColumn.SuggestedOrder]: {
    id: ProductTableColumn.SuggestedOrder,
    columnTitle: "Suggested order",
    columnChooserTitle: "Suggested order",
    tooltip: "The recommended amount of SKUs to reorder based on demand and SKU settings.",
    keyWords: ["recommended order", "order suggestion", "suggested order"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.ExistingPO]: {
    id: ProductTableColumn.ExistingPO,
    columnTitle: "Existing PO",
    columnChooserTitle: "Existing PO",
    tooltip: "Indicates a purchase order with a delayed arrival date.",
    keyWords: ["existing po", "delayed po", "purchase order warning"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.ColliSize]: {
    id: ProductTableColumn.ColliSize,
    columnTitle: "Colli size",
    columnChooserTitle: "Colli size",
    tooltip:
      "The number of units in a single package or shipping unit. Can be adjusted in SKU settings.",
    keyWords: ["package size", "packaging unit", "colli dimensions", "colli size"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.Buffer]: {
    id: ProductTableColumn.Buffer,
    columnTitle: "Safety stock",
    columnChooserTitle: "Safety Stock",
    tooltip:
      "The safety stock held to cover unforeseen demand spikes. Can be adjusted in SKU settings.",
    keyWords: ["reserve stock", "safety stock", "buffer level", "buffer"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.WeeksOnHand]: {
    id: ProductTableColumn.WeeksOnHand,
    columnTitle: "Weeks on hand",
    columnChooserTitle: "Weeks on hand",
    tooltip: `The number of weeks the current stock will last based on expected ${formatName("sale", true)} demand.`,
    keyWords: ["stock duration", "inventory weeks", "weeks of supply", "weeks on hand"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.Sales]: {
    id: ProductTableColumn.Sales,
    columnTitle: formatName("sale", true, true),
    columnChooserTitle: "Sales (Last 52 weeks)",
    columnSubtitle: "Last 52 weeks",
    tooltip: `Gross ${formatName("sale", true)} in the ${formatName("sale", true)} period.`,
    keyWords: [
      "units sold",
      "sales amount",
      "sales volume",
      "historic sales",
      "sales",
      formatName("sale"),
      formatName("sale", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.PreOrders]: {
    id: ProductTableColumn.PreOrders,
    columnTitle: `Future ${formatName("preorder", true)}`,
    columnChooserTitle: `Future ${formatName("preorder", false, true)}`,
    tooltip: `Amount of ${formatName("preorder", true)} that are yet to be fulfilled`,
    keyWords: [
      "future sales",
      "preorders",
      "pre-orders",
      "pre orders",
      "future preorders",
      "future pre-orders",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.PreOrdersLastYear]: {
    id: ProductTableColumn.PreOrdersLastYear,
    columnTitle: formatName("preorder", true, true),
    columnChooserTitle: `${formatName("preorder", true, true)} (Last 52 weeks)`,
    columnSubtitle: "Last 52 weeks",
    tooltip: `Amount of ${formatName("preorder", true)} that were placed in the previous year`,
    keyWords: [
      "future sales",
      "preorders",
      "pre-orders",
      "pre orders",
      "future preorders",
      "future pre-orders",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.Forecast]: {
    id: ProductTableColumn.Forecast,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    columnSubtitle: "Next 52 weeks",
    tooltip: "The expected demand quantity to sell during the period.",
    keyWords: [
      "predicted sales",
      "sales forecast",
      "projection",
      "expected sales",
      "forecast",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.StockLevel]: {
    id: ProductTableColumn.StockLevel,
    columnTitle: "Stock level",
    columnChooserTitle: "Stock level",
    tooltip: "The current amount of stock on hand that is not reserved.",
    keyWords: ["inventory level", "stock amount", "stock level"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.ReservedStockLevel]: {
    id: ProductTableColumn.ReservedStockLevel,
    columnTitle: "Reserved stock",
    columnChooserTitle: "Reserved stock",
    tooltip: "Stock reserved for placed orders.",
    keyWords: ["inventory level", "stock amount", "reserved stock level"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.AvailableToSell]: {
    id: ProductTableColumn.AvailableToSell,
    columnTitle: formatName("available_quantity", false, true),
    columnChooserTitle: formatName("available_quantity", false, true),
    tooltip: `The ${formatName("available_quantity")} quantity reported per SKU and warehouse.`,
    keyWords: ["ats", "trading stock", "availability", formatName("available_quantity")],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.InventoryValue]: {
    id: ProductTableColumn.InventoryValue,
    columnTitle: "Inventory value",
    columnChooserTitle: "Inventory value",
    tooltip: "The monetary value of the current inventory using the latest cost price.",
    keyWords: ["stock value", "current inventory worth", "inventory cost", "inventory value"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.CostPrice]: {
    id: ProductTableColumn.CostPrice,
    columnTitle: "Cost price",
    columnChooserTitle: "Cost price",
    tooltip: "The latest cost price from the supplier.",
    keyWords: ["product cost", "cost to company", "purchase price", "cost price", "cogs"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.RecommendedRetailPrice]: {
    id: ProductTableColumn.RecommendedRetailPrice,
    columnTitle: "Recommended retail price",
    columnChooserTitle: "Recommended retail price",
    tooltip: "The recommended retail (B2C) price. Blank when not available for the product.",
    keyWords: ["rrp", "retail price", "recommended retail price", "msrp", "b2c price"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.WholesalePrice]: {
    id: ProductTableColumn.WholesalePrice,
    columnTitle: "Wholesale price",
    columnChooserTitle: "Wholesale price",
    tooltip: "The wholesale (B2B) price. Blank when not available for the product.",
    keyWords: ["wholesale price", "b2b price", "trade price"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.LeadTime]: {
    id: ProductTableColumn.LeadTime,
    columnTitle: "Total lead time",
    columnChooserTitle: "Total lead time",
    tooltip:
      "The full time from placing an order to receiving it in stock, covering production and transport. Can be adjusted in SKU settings.",
    keyWords: [
      "total lead time",
      "lead time",
      "production lead time",
      "transport lead time",
      "delivery time",
      "supply time",
      "time to deliver",
    ],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.ServiceLevel]: {
    id: ProductTableColumn.ServiceLevel,
    columnTitle: "Service level",
    columnChooserTitle: "Service level",
    tooltip: "The target probability of not running out of stock. Can be adjusted in SKU settings.",
    keyWords: ["service quality", "service target", "service efficiency", "service level"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.SoldSince]: {
    id: ProductTableColumn.SoldSince,
    columnTitle: "Sold since",
    columnChooserTitle: "Sold since",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    tooltip: `Date of first sale.`,
    keyWords: [
      "first sale date",
      "initial sale",
      "start of sales",
      "sold since",
      `first ${formatName("sale")} date`,
      `start of ${formatName("sale", true)}`,
      `initial ${formatName("sale")}`,
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.WeekCover]: {
    id: ProductTableColumn.WeekCover,
    columnTitle: "Week cover",
    columnSubtitle: "Safety stock",
    columnChooserTitle: "Week cover (Safety stock)",
    tooltip:
      "A safety stock measure of the number of weeks that should remain when inventory is replenished. Will only appear if safety stock is set as week cover in SKU settings.",
    keyWords: [
      "coverage weeks",
      "stock cover weeks",
      "inventory cover",
      "week cover",
      "safety stock",
    ],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.SalesGrowth]: {
    id: ProductTableColumn.SalesGrowth,
    columnTitle: `${formatName("sale", true, true)} growth`,
    columnChooserTitle: `${formatName("sale", true, true)} growth (%)`,
    tooltip: `The percentage increase in gross ${formatName("sale", true)} value over time (based on gross units x cost price).`,
    keyWords: [
      "sales growth",
      "growth rate",
      "increase in sales",
      "sales increase",
      "growth",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ForecastedGrowth]: {
    id: ProductTableColumn.ForecastedGrowth,
    columnTitle: "Expected growth (%)",
    columnChooserTitle: "Expected growth (%)",
    columnSubtitle: "Next 52 weeks",
    tooltip: `The percentage increase in expected demand over time.`,
    keyWords: [
      "expected growth",
      "expected growth rate",
      "forecasted growth",
      "forecasted growth rate",
      "increase in sales",
      "forecast increase",
      "growth",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.PurchaseInterval]: {
    id: ProductTableColumn.PurchaseInterval,
    columnTitle: "Purchase interval",
    columnChooserTitle: "Purchase interval",
    tooltip: "The regularity of placing orders for restocking. Can be adjusted in SKU settings.",
    keyWords: ["buying interval", "order frequency", "purchase frequency", "purchase interval"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.HideColumns]: {
    id: ProductTableColumn.HideColumns,
    columnTitle: "Hide Columns",
    columnChooserTitle: "Hide Columns",
    group: TableColumnGroups.Other,
  },
  [ProductTableColumn.IncomingStock]: {
    id: ProductTableColumn.IncomingStock,
    columnTitle: "Incoming stock",
    columnChooserTitle: "Incoming stock",
    tooltip: "Quantity of stock on order.",
    keyWords: ["arriving stock", "incoming inventory", "stock on the way", "incoming stock"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.LostSales]: {
    id: ProductTableColumn.LostSales,
    columnTitle: `Lost ${formatName("sale", true)}`,
    columnChooserTitle: `Lost ${formatName("sale", true)}`,
    columnSubtitle: "Within lead time",
    tooltip: `Unrealized gross ${formatName("sale", true)}: the quantity of ${formatName("sale", true)} lost to stockouts within lead time.`,
    keyWords: [
      "missed sales",
      "unfulfilled demand",
      "sales shortfall",
      "lost sales",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.Profit]: {
    id: ProductTableColumn.Profit,
    columnTitle: "Profit",
    columnChooserTitle: "Profit",
    columnSubtitle: "Last 52 weeks",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    tooltip: `Approximate profit based on actual sales prices, and the latest cost price.`,
    keyWords: [
      "units sold",
      "sales amount",
      "sales volume",
      "product cost",
      "profit",
      "margin",
      formatName("sale"),
      formatName("sale", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.OutOfStockDate]: {
    id: ProductTableColumn.OutOfStockDate,
    columnTitle: "Out of stock date",
    columnChooserTitle: "Out of stock date",
    columnSubtitle: "Next",
    tooltip: "The projected date when the product will be sold out.",
    keyWords: [
      "stock out date",
      "depletion date",
      "no stock date",
      "stockout date",
      "out of stock date",
    ],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.SafetyStockOutDate]: {
    id: ProductTableColumn.SafetyStockOutDate,
    columnTitle: "Safety stock limit date",
    columnChooserTitle: "Safety stock limit date",
    tooltip: "The projected date when inventory will drop below the safety stock level.",
    keyWords: ["safety stock depletion", "depletion date", "safety stock out date"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.OptimalOrderDate]: {
    id: ProductTableColumn.OptimalOrderDate,
    columnTitle: "Optimal order date",
    columnChooserTitle: "Optimal order date",
    columnSubtitle: "Next",
    tooltip: "The last date to place an order to avoid using the safety stock.",
    keyWords: [
      "order deadline",
      "last order date",
      "optimal order time",
      "buy date",
      "optimal order date",
    ],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.StockStatus]: {
    id: ProductTableColumn.StockStatus,
    columnTitle: "Stock Status",
    columnChooserTitle: "Stock Status",
    tooltip: "The current availability status (stockout, reorder, fine, overstock).",
    keyWords: ["inventory status", "availability status", "stock condition", "stock status"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.MinimumOrderQuantity]: {
    id: ProductTableColumn.MinimumOrderQuantity,
    columnTitle: "SKU MOQ",
    columnChooserTitle: "SKU MOQ",
    tooltip:
      "The smallest amount of a SKU that can be ordered from the supplier. Can be adjusted in SKU settings.",
    keyWords: [
      "minimum purchase",
      "smallest order",
      "minimum buy",
      "moq",
      "minimum order quantity",
    ],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.SecondaryMOQ]: {
    id: ProductTableColumn.SecondaryMOQ,
    columnTitle: secondaryMoqName,
    columnChooserTitle: secondaryMoqName,
    tooltip: `Secondary MOQ, the MOQ on ${secondaryMoqName}`,
    keyWords: [
      "minimum purchase",
      "smallest order",
      "minimum buy",
      "moq",
      "minimum order quantity",
      "secondary moq",
    ],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.AvailabilityWithinLeadTime]: {
    id: ProductTableColumn.AvailabilityWithinLeadTime,
    columnTitle: "Availability",
    columnChooserTitle: "Availability (Within lead time)",
    columnSubtitle: "Within lead time",
    tooltip:
      "The percentage of demand that can be met within the lead time with current inventory, weighted by forecast.",
    keyWords: ["lead time availability", "availability forecast", "availability within lead time"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.LocationAvailability]: {
    id: ProductTableColumn.LocationAvailability,
    columnTitle: "Location availability",
    columnChooserTitle: "Location availability",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    tooltip: `The percentage of the skus that are available for sale in each storage location, weighted by expected demand.`,
    keyWords: [
      "current availability",
      "in stock today",
      "today's status",
      "location stock",
      "location availability",
    ],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.LocationMinimumStockLevel]: {
    id: ProductTableColumn.LocationMinimumStockLevel,
    columnTitle: "Minimum location stock",
    columnChooserTitle: "Minimum location stock",
    tooltip: `Minimum stock level on storage locations, currently sendable by ERP-systems`,
    keyWords: ["location stock", "location availability"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.ForecastModel]: {
    id: ProductTableColumn.ForecastModel,
    columnTitle: "Forecast model",
    columnChooserTitle: "Forecast model",
    tooltip: `The forecast algorithm used to predict ${formatName("sale", true)}.`,
    keyWords: ["prediction model", "forecasting method", "projection model", "forecast model"],
    group: TableColumnGroups.ModelsAndAnalysis,
  },
  [ProductTableColumn.PreferredVendor]: {
    id: ProductTableColumn.PreferredVendor,
    columnTitle: "Preferred Vendor",
    columnChooserTitle: "Preferred Vendor",
    tooltip: "The preferred supplier for the product.",
    keyWords: ["seller", "supplier", "distributor", "vendor", "preferred vendor"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.ABC]: {
    id: ProductTableColumn.ABC,
    columnTitle: "ABC",
    columnChooserTitle: "ABC",
    tooltip: `Classification of products based on revenue, where A represents the top ${convertPercentileToPercentLabel("A")} revenue contributors, B the next ${convertPercentileToPercentLabel("B")}, and C the remaining ${convertPercentileToPercentLabel("C")} products.`,
    keyWords: ["abc analysis", "sales classification", "abc"],
    group: TableColumnGroups.ModelsAndAnalysis,
  },
  [ProductTableColumn.Vendor]: {
    id: ProductTableColumn.Vendor,
    columnTitle: "Vendor",
    columnChooserTitle: "Vendor",
    tooltip: "The company supplying the product to your business.",
    keyWords: ["seller", "supplier", "distributor", "vendor"],
    group: TableColumnGroups.ProductInformation,
  },
  // TODO(BUD-370): rename to "Current lifecycle status".
  [ProductTableColumn.LifecycleStatus]: {
    id: ProductTableColumn.LifecycleStatus,
    columnTitle: "Lifecycle status",
    columnChooserTitle: "Lifecycle status",
    tooltip: "The products lifecycle status, indicating whether it's active, discontinued, etc.",
    keyWords: [
      "lifecycle status",
      "product status",
      "active",
      "discontinued",
      "lifecycle",
      "phased out",
      "phasing out",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SupplierSku]: {
    id: ProductTableColumn.SupplierSku,
    columnTitle: "Supplier SKU",
    columnChooserTitle: "Supplier SKU",
    tooltip: "The supplier's stock-keeping unit (SKU) for this item.",
    keyWords: ["supplier code", "supplier identifier", "supplier sku", "vendor sku", "identifier"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.ProductType]: {
    id: ProductTableColumn.ProductType,
    columnTitle: "Product type",
    columnChooserTitle: "Product type",
    tooltip:
      "The type of the product. Can be 'NOOS' or 'Fashion.' Fashion products assigned to a collection will have a target stockout date when the collection period ends, while NOOS products will continue beyond a collection.",
    keyWords: ["product type", "noos", "fashion", "seasonal"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.SellThroughRate]: {
    id: ProductTableColumn.SellThroughRate,
    columnTitle: "Sell-through rate",
    columnChooserTitle: "Sell-through rate",
    tooltip: "The percentage of stock sold relative to the stock received.",
    keyWords: ["sell through rate", "sellthrough rate"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.InventoryTurn]: {
    id: ProductTableColumn.InventoryTurn,
    columnTitle: "Inventory turn",
    columnChooserTitle: "Inventory turn",
    columnSubtitle: "Last 12 months",
    tooltip:
      "Cost of goods sold over the last 12 months divided by average monthly inventory value.",
    keyWords: ["inventory turn", "stock turn", "turnover", "turn rate"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.GMROI]: {
    id: ProductTableColumn.GMROI,
    columnTitle: "GMROI",
    columnChooserTitle: "Gross Margin Return on Investment",
    columnSubtitle: "Last 12 months",
    tooltip: "Gross Margin Return on Investment.",
    keyWords: [
      "gmroi",
      "gross margin return on investment",
      "return on inventory investment",
      "return on investment",
      "roi",
    ],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.Collection]: {
    id: ProductTableColumn.Collection,
    columnTitle: "Collection",
    columnChooserTitle: "Collection",
    tooltip: "The collection a product is assigned to.",
    keyWords: ["group", "set", "category", "collection", "season"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.Revenue]: {
    id: ProductTableColumn.Revenue,
    columnTitle: "Revenue",
    columnChooserTitle: "Revenue",
    columnSubtitle: "Last 52 weeks",
    // eslint-disable-next-line @hakio/no-untranslated-terminology
    tooltip: `The revenue in the sales period.`,
    keyWords: [
      "sales income",
      "sales earnings",
      "sales value",
      "sales revenue",
      "revenue",
      formatName("sale", true),
      formatName("sale"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.Returns]: {
    id: ProductTableColumn.Returns,
    columnTitle: "Returns",
    columnChooserTitle: "Returns",
    columnSubtitle: "Last 52 weeks",
    tooltip: "The number of units returned by customers.",
    keyWords: ["returned units", "product returns", "customer refunds", "returns"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ExpectedReturns]: {
    id: ProductTableColumn.ExpectedReturns,
    columnTitle: "Expected Returns",
    columnChooserTitle: "Expected Returns",
    columnSubtitle: "Next 52 weeks",
    tooltip: "Forecasted number of units expected to be returned over the next 52 weeks.",
    keyWords: ["expected returns", "forecasted returns", "future returns", "predicted returns"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ReturnRate]: {
    id: ProductTableColumn.ReturnRate,
    columnTitle: "Return rate",
    columnChooserTitle: "Return rate (last 52 weeks)",
    columnSubtitle: "Last 52 weeks",
    tooltip: `The percentage of the ${formatName("sale", true)} that were returned over the last 52 weeks.`,
    keyWords: ["return percentage", "return ratio", "return rate", "rate of returns"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ReturnRateWholeHorizon]: {
    id: ProductTableColumn.ReturnRateWholeHorizon,
    columnTitle: "Return rate",
    columnChooserTitle: "Return rate (full horizon)",
    columnSubtitle: "Full horizon",
    tooltip: `The percentage of the ${formatName("sale", true)} that were returned over the full horizon.`,
    keyWords: [
      "return percentage",
      "return ratio",
      "return rate",
      "rate of returns",
      "full horizon",
      "whole horizon",
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.OverstockValue]: {
    id: ProductTableColumn.OverstockValue,
    columnTitle: "Overstock value",
    columnChooserTitle: "Overstock value",
    tooltip: "The cost of the stock that exceeds the overstock threshold.",
    keyWords: ["excess stock value", "surplus value", "overstock value"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [ProductTableColumn.SafetyStockValue]: {
    id: ProductTableColumn.SafetyStockValue,
    columnTitle: "Safety stock value",
    columnChooserTitle: "Safety stock value",
    tooltip: "The cost of the suggested safety stock.",
    keyWords: ["safety stock value", "safety stock cost", "safety stock value"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [ProductTableColumn.NetSales]: {
    id: ProductTableColumn.NetSales,
    columnTitle: `Net ${formatName("sale", true, true)}`,
    columnChooserTitle: "Net Sales (Last 52 weeks)",
    columnSubtitle: "Last 52 weeks",
    tooltip: `Net ${formatName("sale", true)} for the last 52 weeks (${formatName("sale", true, true)} minus Returns).`,
    keyWords: [
      "net sales",
      "sales minus returns",
      "returns",
      "gross sales",
      formatName("sale"),
      formatName("sale", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.SalesLast30Days]: {
    id: ProductTableColumn.SalesLast30Days,
    columnTitle: formatName("sale", true, true),
    columnChooserTitle: "Sales (Last 30 days)",
    columnSubtitle: "Last 30 days",
    tooltip: "Gross units sold in the last 30 days.",
    keyWords: [
      "units sold",
      "sales amount",
      "sales volume",
      "historic sales",
      "sales",
      "last 30 days",
      "month",
      formatName("sale"),
      formatName("sale", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ActivatedFrom]: {
    id: ProductTableColumn.ActivatedFrom,
    columnTitle: "Activated from",
    columnChooserTitle: "Activated from",
    tooltip: "The date the SKU was activated from, and forecasting started.",
    keyWords: ["activation date", "start date", "activated from", "forecast start date"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.ActivatedUntil]: {
    id: ProductTableColumn.ActivatedUntil,
    columnTitle: "Activated until",
    columnChooserTitle: "Activated until",
    tooltip: "The date the SKU is activated until, and forecasting ending.",
    keyWords: ["activation date", "end date", "activated until", "forecast end date"],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.PhasingOutFrom]: {
    id: ProductTableColumn.PhasingOutFrom,
    columnTitle: "Phasing out from",
    columnChooserTitle: "Phasing out from",
    tooltip:
      "Scheduled date to stop buying. The product is marked as 'phasing out' from this date.",
    keyWords: [
      "phase out",
      "phasing out",
      "phase out date",
      "phasing out from",
      "discontinue",
      "end of life",
      "stop buying",
      "sell down",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  [ProductTableColumn.ForecastQuality]: {
    id: ProductTableColumn.ForecastQuality,
    columnTitle: "Forecast quality",
    columnChooserTitle: "Forecast quality",
    tooltip: `How closely Hakio's past forecasts matched ${formatName("sale", true)}, scored 0 to 100. Low is under ${FORECAST_QUALITY_THRESHOLDS.LOW_TO_MODERATE}, high is ${FORECAST_QUALITY_THRESHOLDS.MODERATE_TO_HIGH} or more. Hover for the 1, 3, 6 and 12 month scores.`,
    keyWords: ["fit quality", "forecast accuracy", "model fit", "forecast quality", "quality"],
    group: TableColumnGroups.ModelsAndAnalysis,
  },
  [ProductTableColumn.ForecastedGrowthAlert]: {
    id: ProductTableColumn.ForecastedGrowthAlert,
    columnTitle: "Demand alert",
    columnChooserTitle: "Demand alert",
    tooltip: `Highlights large changes in expected demand vs last 52 weeks ${formatName("sale", true)} and provides an explanation for the change.`,
    keyWords: [
      "growth alert",
      "demand alert",
      "forecast alert",
      "growth warning",
      "model forecast",
    ],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastInfo]: {
    id: ProductTableColumn.ForecastInfo,
    columnTitle: "Demand info",
    columnChooserTitle: "Demand info",
    tooltip: `Shows informational statuses like adjusted demand, short ${formatName("sale", true)} horizons, and short demand horizons.`,
    keyWords: [
      "demand info",
      "forecast info",
      "adjusted",
      "short sales horizon",
      "short forecast horizon",
    ],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastedGrowthUnits]: {
    id: ProductTableColumn.ForecastedGrowthUnits,
    columnTitle: "Expected growth",
    columnChooserTitle: "Expected growth (units)",
    columnSubtitle: "Next 52 weeks",
    tooltip: `The absolute change in expected demand compared to the last 52 weeks.`,
    keyWords: [
      "expected growth absolute",
      "forecasted growth absolute",
      "forecast change",
      "growth amount",
      "growth sum",
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.SalesWithoutPreOrders]: {
    id: ProductTableColumn.SalesWithoutPreOrders,
    columnTitle: `${formatName("sale", true, true)} w/o ${formatName("preorder", true)}`,
    columnChooserTitle: "Reorders (Last 52 weeks)",
    columnSubtitle: "Last 52 weeks",
    tooltip: `Gross ${formatName("sale", true, true)} last 52 weeks minus ${formatName("preorder", true)} last 52 weeks.`,
    keyWords: ["reorders", "sales without pre-orders", "organic sales", "sales minus pre-orders"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastWithoutKnownPreOrders]: {
    id: ProductTableColumn.ForecastWithoutKnownPreOrders,
    columnTitle: `Forecasted reorders`,
    columnChooserTitle: `Forecasted reorders (Next 52 weeks)`,
    columnSubtitle: "Next 52 weeks",
    tooltip: `Forecasted reorders for the next 52 weeks: expected demand excluding both known and forecasted ${formatName("preorder", true)}.`,
    keyWords: ["forecasted reorders", "reorders", "risk buy", "gambling", "risk", "uncertainty"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.PreOrderFulfillment]: {
    id: ProductTableColumn.PreOrderFulfillment,
    columnTitle: `Projected ${formatName("preorder")} fulfillment`,
    columnChooserTitle: `Projected ${formatName("preorder", true)} fulfillment`,
    tooltip: `Projected fulfillment % of future ${formatName("preorder", true)}`,
    keyWords: [
      "future sales",
      "preorders",
      "pre-orders",
      "pre orders",
      "future preorders",
      "future pre-orders",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.UnfulfillablePreorder]: {
    id: ProductTableColumn.UnfulfillablePreorder,
    columnTitle: formatName("unfulfillable_preorder", true, true),
    columnChooserTitle: formatName("unfulfillable_preorder", true, true),
    tooltip: getUnfulfillableDescriptionShort(),
    keyWords: [
      "unfulfillable",
      "shortfall",
      "backorder",
      "preorders",
      "pre-orders",
      formatName("unfulfillable_preorder"),
      formatName("unfulfillable_preorder", true),
      formatName("preorder", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.AtRiskPreorder]: {
    id: ProductTableColumn.AtRiskPreorder,
    columnTitle: formatName("at_risk_preorder", true, true),
    columnChooserTitle: formatName("at_risk_preorder", true, true),
    tooltip: getAtRiskDescriptionShort(),
    keyWords: [
      "at risk",
      "at risk preorder",
      "preorders",
      "pre-orders",
      formatName("at_risk_preorder"),
      formatName("at_risk_preorder", true),
      formatName("preorder", true),
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [ProductTableColumn.ForecastWithoutPreOrders]: {
    id: ProductTableColumn.ForecastWithoutPreOrders,
    columnTitle: `Forecasted reorders`,
    columnChooserTitle: `Forecasted reorders`,
    columnSubtitle: "Next 52 weeks",
    tooltip: `Forecasted reorders for the next 52 weeks: expected demand excluding both known and forecasted ${formatName("preorder", true)}.`,
    keyWords: ["forecasted reorders", "reorders", "gambling", "risk", "uncertainty"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.GrowthWithoutPreOrders]: {
    id: ProductTableColumn.GrowthWithoutPreOrders,
    columnTitle: `Reorder growth`,
    columnChooserTitle: `Reorder growth (%)`,
    columnSubtitle: "%",
    tooltip: `Percentage growth comparing forecasted reorders to ${formatName("sale", true)} w/o ${formatName("preorder", true)}`,
    keyWords: ["reorder growth", "growth without pre-orders", "organic growth", "growth percent"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.StockoutImputation]: {
    id: ProductTableColumn.StockoutImputation,
    columnTitle: `Estimated demand during stockout`,
    columnChooserTitle: `Estimated demand during stockout`,
    columnSubtitle: "Last year",
    tooltip: "Total quantity added to expected demand due to stockouts in the last 52 weeks",
    keyWords: ["stockout", "imputation", "stock out", "added quantity"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.MissingSalesImputation]: {
    id: ProductTableColumn.MissingSalesImputation,
    columnTitle: `Estimated un-observed demand (last 52 weeks)`,
    columnChooserTitle: `Estimated un-observed demand (last 52 weeks)`,
    columnSubtitle: "Last year",
    tooltip: `Estimated quantity of un-observed demand in the last 52 weeks`,
    keyWords: [
      "un-observed sales",
      "unobserved sales",
      "estimated",
      "missing sales",
      "imputation",
      "missing data",
      "added quantity",
    ],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastSalesVsLy1m]: {
    id: ProductTableColumn.ForecastSalesVsLy1m,
    columnTitle: `${formatName("sale", true, true)} vs LY (last 5W)`,
    columnChooserTitle: `${formatName("sale", true, true)} vs LY (last 5W)`,
    tooltip: `Percentage change of the last 5 weeks of ${formatName("sale", true)} vs the same period last year.`,
    keyWords: ["sales vs ly", "vs last year", "5w", "1m", "recent trend", "yoy", "sales index"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastSalesVsLy3m]: {
    id: ProductTableColumn.ForecastSalesVsLy3m,
    columnTitle: `${formatName("sale", true, true)} vs LY (last 13W)`,
    columnChooserTitle: `${formatName("sale", true, true)} vs LY (last 13W)`,
    tooltip: `Percentage change of the last 13 weeks of ${formatName("sale", true)} vs the same period last year.`,
    keyWords: ["sales vs ly", "vs last year", "13w", "3m", "quarter trend", "yoy", "sales index"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastSalesVsLy6m]: {
    id: ProductTableColumn.ForecastSalesVsLy6m,
    columnTitle: `${formatName("sale", true, true)} vs LY (last 26W)`,
    columnChooserTitle: `${formatName("sale", true, true)} vs LY (last 26W)`,
    tooltip: `Percentage change of the last 26 weeks of ${formatName("sale", true)} vs the same period last year.`,
    keyWords: ["sales vs ly", "vs last year", "26w", "6m", "half year trend", "yoy", "sales index"],
    group: TableColumnGroups.ForecastMetrics,
  },
  [ProductTableColumn.ForecastSalesVsLy12m]: {
    id: ProductTableColumn.ForecastSalesVsLy12m,
    columnTitle: `${formatName("sale", true, true)} vs LY (last 52W)`,
    columnChooserTitle: `${formatName("sale", true, true)} vs LY (last 52W)`,
    tooltip: `Percentage change of the last 52 weeks of ${formatName("sale", true)} vs the same period last year.`,
    keyWords: ["sales vs ly", "vs last year", "52w", "12m", "annual trend", "yoy", "sales index"],
    group: TableColumnGroups.ForecastMetrics,
  },
}

const collectionColumnNaming: Record<CollectionTableColumn, ColumnNaming> = {
  [CollectionTableColumn.BelongingToCollection]: {
    id: CollectionTableColumn.BelongingToCollection,
    columnTitle: "Collections",
    columnChooserTitle: "Belonging to collection",
    columnSubtitle: "belonging to",
    tooltip: "The collections a product belongs to",
    keyWords: ["collection", "belonging to", "group", "set", "season"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.Forecast]: {
    id: CollectionTableColumn.Forecast,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    tooltip: "Expected demand for collection period",
    keyWords: ["demand", "forecast", "prediction", "collection demand"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.PreOrders]: {
    id: CollectionTableColumn.PreOrders,
    columnTitle: formatName("preorder", true, true),
    columnChooserTitle: formatName("preorder", true, true),
    columnSubtitle: "Collection period",
    tooltip: `${formatName("preorder", true, true)} for collection period`,
    keyWords: ["pre-orders", "preorders", "preorder", "collection pre-orders", "collection"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.InboundPreOrders]: {
    id: CollectionTableColumn.InboundPreOrders,
    columnTitle: `Inbound ${formatName("preorder", true)}`,
    columnChooserTitle: `Collection inbound ${formatName("preorder", true)}`,
    tooltip: `Inbound ${formatName("preorder", true)} for the collection period`,
    keyWords: ["inbound pre-orders", "incoming pre-orders", "collection inbound pre-orders"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.PreOrdersWithoutInbound]: {
    id: CollectionTableColumn.PreOrdersWithoutInbound,
    columnTitle: `${formatName("preorder", true, true)} without inbound`,
    columnChooserTitle: `Collection ${formatName("preorder", true)} without inbound`,
    tooltip: `${formatName("preorder", true, true)} without inbound for the collection period`,
    keyWords: ["pre-orders without inbound", "collection pre-orders without inbound"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.FuturePreOrders]: {
    id: CollectionTableColumn.FuturePreOrders,
    columnTitle: `Future ${formatName("preorder", true)}`,
    columnChooserTitle: `Collection future ${formatName("preorder", true)}`,
    tooltip: `Future ${formatName("preorder", true)} (not yet delivered) for the collection period`,
    keyWords: ["future pre-orders", "future preorders", "collection future pre-orders"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.Sales]: {
    id: CollectionTableColumn.Sales,
    columnTitle: formatName("sale", true, true),
    columnChooserTitle: `Collection ${formatName("sale", true)}`,
    tooltip: `Gross ${formatName("sale", true)} for the collection period.`,
    keyWords: ["sales", "revenue", "collection sales"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.SalesAmount]: {
    id: CollectionTableColumn.SalesAmount,
    columnTitle: `${formatName("sale", true, true)} amount`,
    columnChooserTitle: `Collection ${formatName("sale", true)} amount`,
    tooltip: `Gross ${formatName("sale", true)} amount for the collection period.`,
    keyWords: ["sales amount", "revenue", "currency", "collection sales amount"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.PreOrdersAmount]: {
    id: CollectionTableColumn.PreOrdersAmount,
    columnTitle: `${formatName("preorder", true, true)} amount`,
    columnChooserTitle: `Collection ${formatName("preorder", true)} amount`,
    tooltip: `${formatName("preorder", true, true)} amount for the collection period`,
    keyWords: ["pre-orders amount", "preorders amount", "currency", "collection pre-orders amount"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.Out]: {
    id: CollectionTableColumn.Out,
    columnTitle: "Out",
    columnChooserTitle: "Collection out",
    columnSubtitle: "of collection",
    tooltip: "Number of SKUs has been removed from the collection.",
    keyWords: ["out", "removed", "excluded", "collection out"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.PreOrdersComparison]: {
    id: CollectionTableColumn.PreOrdersComparison,
    columnTitle: `Collection ${formatName("preorder", true)} comparison`,
    columnChooserTitle: `Collection ${formatName("preorder", true)} comparison`,
    tooltip: `${formatName("preorder", true, true)} for the comparison collection`,
    keyWords: ["pre-orders comparison", "preorders comparison", "collection pre-orders comparison"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.InboundPreOrdersComparison]: {
    id: CollectionTableColumn.InboundPreOrdersComparison,
    columnTitle: `Collection inbound ${formatName("preorder", true)} comparison`,
    columnChooserTitle: `Collection inbound ${formatName("preorder", true)} comparison`,
    tooltip: `Inbound ${formatName("preorder", true)} for the comparison collection`,
    keyWords: [
      "inbound pre-orders comparison",
      "incoming pre-orders comparison",
      "collection inbound pre-orders comparison",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.PreOrdersWithoutInboundComparison]: {
    id: CollectionTableColumn.PreOrdersWithoutInboundComparison,
    columnTitle: `Collection ${formatName("preorder", true)} without inbound comparison`,
    columnChooserTitle: `Collection ${formatName("preorder", true)} without inbound comparison`,
    tooltip: `${formatName("preorder", true, true)} without inbound for the comparison collection`,
    keyWords: [
      "pre-orders without inbound comparison",
      "collection pre-orders without inbound comparison",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.SalesComparison]: {
    id: CollectionTableColumn.SalesComparison,
    columnTitle: `Collection ${formatName("sale", true)} comparison`,
    columnChooserTitle: `Collection ${formatName("sale", true)} comparison`,
    tooltip: `Gross ${formatName("sale", true)} for the comparison collection.`,
    keyWords: ["sales comparison", "revenue comparison", "collection sales comparison"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ForecastComparison]: {
    id: CollectionTableColumn.ForecastComparison,
    columnTitle: "Demand comparison",
    columnChooserTitle: "Demand comparison",
    tooltip: "Expected demand for the comparison collection",
    keyWords: [
      "demand comparison",
      "forecast comparison",
      "prediction comparison",
      "collection demand comparison",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ProjectedSales]: {
    id: CollectionTableColumn.ProjectedSales,
    columnTitle: `Total demand`,
    columnChooserTitle: `Projected collection ${formatName("sale", true)}`,
    tooltip: `A combination of ${formatName("sale", true)} and expected demand for the collection period`,
    keyWords: ["projected sales", "total sales", "collection sales", "projected demand"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ProjectedSalesComparison]: {
    id: CollectionTableColumn.ProjectedSalesComparison,
    columnTitle: `Projected collection ${formatName("sale", true)} comparison`,
    columnChooserTitle: `Projected collection ${formatName("sale", true)} comparison`,
    tooltip: `A combination of gross ${formatName("sale", true)} and expected demand for the comparison collection.`,
    keyWords: [
      "projected sales comparison",
      "total sales comparison",
      "collection sales comparison",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ProjectedSalesWithoutPreOrders]: {
    id: CollectionTableColumn.ProjectedSalesWithoutPreOrders,
    columnTitle: `Total demand w/o ${formatName("preorder", true)}`,
    columnChooserTitle: `Projected collection ${formatName("sale", true)} w/o ${formatName("preorder", true)}`,
    tooltip: `Combined gross ${formatName("sale", true)} and demand for the collection period minus the ${formatName("preorder", true)}.`,
    keyWords: [
      "projected sales without pre-orders",
      "total sales without pre-orders",
      "risk buy",
      "gambling",
      "risk",
      "uncertainty",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ProjectedStockLevel]: {
    id: CollectionTableColumn.ProjectedStockLevel,
    columnTitle: "Projected Stock Level",
    columnChooserTitle: `Projected collection stock level`,
    tooltip: `Projected stock level for the end of the collection period`,
    keyWords: ["estimated stock level", "projected stock level", "collection stock level"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ProjectedInventoryValue]: {
    id: CollectionTableColumn.ProjectedInventoryValue,
    columnTitle: "Projected Inventory Value",
    columnChooserTitle: `Projected collection inventory value`,
    tooltip: `Projected inventory value for the end of the collection period`,
    keyWords: [
      "estimated inventory value",
      "projected inventory value",
      "collection inventory value",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.IncomingOrders]: {
    id: CollectionTableColumn.IncomingOrders,
    columnTitle: "Purchase Orders",
    columnChooserTitle: "Purchase Orders",
    columnSubtitle: "Collection period",
    tooltip: "Purchase Orders for collection period",
    keyWords: [
      "purchase orders",
      "collection purchase orders",
      "incoming orders",
      "incoming",
      "collection",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.IncomingOrdersComparison]: {
    id: CollectionTableColumn.IncomingOrdersComparison,
    columnTitle: `Collection purchase orders comparison`,
    columnChooserTitle: `Collection purchase orders comparison`,
    tooltip: `Purchase orders for the comparison collection`,
    keyWords: [
      "purchase orders comparison",
      "collection purchase orders comparison",
      "incoming orders comparison",
    ],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.Budget]: {
    id: CollectionTableColumn.Budget,
    columnTitle: "Budget",
    columnChooserTitle: `Collection budget`,
    columnSubtitle: "Estimated units",
    tooltip: `Estimated Units`,
    keyWords: ["budget", "allocation", "constraint", "collection budget"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.SalesRisk]: {
    id: CollectionTableColumn.SalesRisk,
    columnTitle: `${formatName("sale", true, true)} risk`,
    columnChooserTitle: `Collection ${formatName("sale", true)} risk`,
    tooltip: `${formatName("sale", true, true)} minus ${formatName("preorder", true)} for the collection period.`,
    keyWords: ["sales risk", "delta", "collection sales risk"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.SalesRiskComparison]: {
    id: CollectionTableColumn.SalesRiskComparison,
    columnTitle: `Collection ${formatName("sale", true)} risk comparison`,
    columnChooserTitle: `Collection ${formatName("sale", true)} risk comparison`,
    tooltip: `${formatName("sale", true, true)} minus ${formatName("preorder", true)} for the comparison collection.`,
    keyWords: ["sales risk comparison", "delta comparison", "collection sales risk comparison"],
    group: TableColumnGroups.Collections,
  },
  [CollectionTableColumn.ExpectedReturns]: {
    id: CollectionTableColumn.ExpectedReturns,
    columnTitle: "Expected Returns",
    columnChooserTitle: "Collection expected returns",
    tooltip: "Forecasted returns for collection period",
    keyWords: ["expected returns", "forecasted returns", "collection returns"],
    group: TableColumnGroups.Collections,
  },
}

const supplyColumnNaming: Record<SupplyTableColumn, ColumnNaming> = {
  [SupplyTableColumn.AlreadyPurchasedQty]: {
    id: SupplyTableColumn.AlreadyPurchasedQty,
    columnTitle: "Already Purchased",
    columnChooserTitle: "Already purchased qty",
    columnSubtitle: "From POs",
    tooltip: "Quantity already purchased associated with this supply plan",
    keyWords: ["purchased", "qty", "quantity"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.ForecastInPeriod]: {
    id: SupplyTableColumn.ForecastInPeriod,
    columnTitle: "Demand",
    columnChooserTitle: "Demand (In period)",
    columnSubtitle: "Collection period",
    tooltip: `Expected demand in the supply plan period. On SKU rows the variant demand is split by each SKU's share of the variant's ${formatName("preorder", true)}; SKUs without ${formatName("preorder", true)} read 0.`,
    keyWords: ["demand", "forecast", "period"],
    // A demand figure, not a supply decision — grouped with the other demand columns.
    group: TableColumnGroups.SalesAndForecast,
  },
  [SupplyTableColumn.TotalDemand]: {
    id: SupplyTableColumn.TotalDemand,
    columnTitle: "Total demand",
    columnChooserTitle: "Total demand (all warehouses)",
    columnSubtitle: "All warehouses",
    tooltip: `Combined demand across every warehouse: known ${formatName("preorder", true)} for warehouses set to ${formatName("preorder", true)} only, and the expected demand everywhere else. Read-only.`,
    keyWords: ["total", "demand", "combined", "all warehouses", "forecast"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.PlannedBuyTotal]: {
    id: SupplyTableColumn.PlannedBuyTotal,
    columnTitle: "Total Planned Buy",
    columnChooserTitle: "Total planned buy",
    columnSubtitle: "All warehouses",
    tooltip: `The quantity you plan to buy, added up across every warehouse. Type the buy in the per-warehouse "Planned buy / Risk %" columns, which follow the warehouse selector. This column reports the total, the difference against Hakio's suggestion, and which warehouse is still short of its ${formatName("preorder", true)}.`,
    keyWords: [
      "planned",
      "buy",
      "total",
      "all warehouses",
      "sum",
      "qty",
      "quantity",
      "purchase",
      "purchase order",
      "supply",
    ],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.RiskStockQty]: {
    id: SupplyTableColumn.RiskStockQty,
    columnTitle: "Risk stock",
    columnChooserTitle: "Risk stock qty",
    tooltip: `Units of projected supply that no ${formatName("preorder", true)} covers. Supply is projected stock at start of period, incoming in period and planned buy.`,
    keyWords: ["risk stock", "risk buy", "gambling", "qty", "quantity"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.RiskStockPct]: {
    id: SupplyTableColumn.RiskStockPct,
    columnTitle: "Risk stock (%)",
    columnChooserTitle: "Risk stock (%)",
    tooltip: `Share of projected supply that no ${formatName("preorder", true)} covers. Supply is projected stock at start of period, incoming in period and planned buy. Clamped to 0% when ${formatName("preorder", true)} exceed supply.`,
    keyWords: ["risk stock", "risk buy", "gambling", "percent", "percentage", "share", "ratio"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.RecommendedRiskBuyPct]: {
    id: SupplyTableColumn.RecommendedRiskBuyPct,
    columnTitle: "Recommended Risk buy (%)",
    columnChooserTitle: "Recommended Risk buy (%)",
    columnSubtitle: "Demand plan",
    tooltip: `The risk buy % benchmark agreed in demand planning. Calculated as (Forecast - ${formatName("preorder", true)}) / Forecast, where both values are snapshotted at the time the demand plan was published. Both are rounded to whole units before the calculation. Read-only, so it does not change when you adjust purchase quantities or when new ${formatName("preorder", true)} arrive. Updates only when the demand plan is re-published.`,
    keyWords: ["risk buy", "recommended", "demand plan", "reference", "percent", "percentage"],
    // A demand-plan benchmark, not a supply-plan figure: it is snapshotted at publish and never
    // moves with the buy, so it belongs with the other demand-plan readings.
    group: TableColumnGroups.ForecastMetrics,
  },
  [SupplyTableColumn.ProjectedStockStartOfPeriod]: {
    id: SupplyTableColumn.ProjectedStockStartOfPeriod,
    columnTitle: "Projected Stock",
    columnChooserTitle: "Projected stock at start of period",
    columnSubtitle: "Start of period",
    tooltip:
      "Stock level at the start of the collection period. Once that date has passed this is the recorded reading from stock history rather than a projection, and the column is titled 'Stock Level'.",
    keyWords: ["projected", "stock", "start", "period", "arrival", "actual", "recorded"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [SupplyTableColumn.IncomingInPeriod]: {
    id: SupplyTableColumn.IncomingInPeriod,
    columnTitle: "Incoming Stock",
    columnChooserTitle: "Incoming stock in period",
    columnSubtitle: "Collection period",
    tooltip:
      "Incoming orders (ERP + sent purchase orders, per company settings) arriving within the collection period. The suggested order is sized against this incoming.",
    keyWords: ["incoming", "stock", "orders", "period", "arrival"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [SupplyTableColumn.SuggestedOrderDelta]: {
    id: SupplyTableColumn.SuggestedOrderDelta,
    columnTitle: SUGGESTED_ORDER_DELTA_TITLE,
    columnSubtitle: SUGGESTED_ORDER_DELTA_SUBTITLE,
    columnChooserTitle: SUGGESTED_ORDER_DELTA_CHOOSER_TITLE,
    tooltip: suggestedOrderDeltaTooltip("Your planned buy"),
    keyWords: SUGGESTED_ORDER_DELTA_KEYWORDS,
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.SuggestedOrderInPeriod]: {
    id: SupplyTableColumn.SuggestedOrderInPeriod,
    columnTitle: "Suggested Order",
    columnChooserTitle: "Suggested order in period",
    columnSubtitle: "Collection period",
    tooltip: "Suggested quantity to be ordered in the supply plan period",
    keyWords: ["suggested", "qty", "quantity", "order"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.TotalPurchaseQty]: {
    id: SupplyTableColumn.TotalPurchaseQty,
    columnTitle: "Total Supply",
    columnChooserTitle: "Total supply",
    tooltip:
      "Total stock expected available for the collection: projected stock at start of period + incoming during period + uncommitted planned buy quantity",
    keyWords: ["total", "qty", "quantity", "supply"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.ProjectedStockEndOfPeriod]: {
    id: SupplyTableColumn.ProjectedStockEndOfPeriod,
    columnTitle: "Projected Stock",
    columnChooserTitle: "Projected stock at end of period",
    columnSubtitle: "End of period",
    tooltip:
      "Stock level at end of period: stock at start of period - forecast + incoming during period + planned buy quantity. Once the period has ended this is instead the recorded closing reading from stock history, and the column is titled 'Actual Stock'.",
    keyWords: ["projected", "stock", "end", "period", "closing", "actual", "recorded"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [SupplyTableColumn.AvailableIncludingNextSeason]: {
    id: SupplyTableColumn.AvailableIncludingNextSeason,
    columnTitle: "Available (incl. next season)",
    columnChooserTitle: "Available (incl. next season)",
    tooltip:
      "Ingested net available stock incl. next season, per SKU (availableQuantityIncludingNextSeason). Negative = unfulfilled orders exceed stock.",
    keyWords: ["available", "next season", "availability", "incl next season"],
    group: TableColumnGroups.ExternalData,
  },
  [SupplyTableColumn.SimulatedAvailability]: {
    id: SupplyTableColumn.SimulatedAvailability,
    columnTitle: "Simulated availability",
    columnChooserTitle: "Simulated availability",
    tooltip:
      "Planned buy + next-season available stock (availableQuantityIncludingNextSeason), per SKU.",
    keyWords: ["simulated availability", "next season", "available quantity"],
    group: TableColumnGroups.ExternalData,
  },
  [SupplyTableColumn.AvailabilityViolation]: {
    id: SupplyTableColumn.AvailabilityViolation,
    columnTitle: "Availability violation",
    columnChooserTitle: "Availability violation",
    tooltip:
      "Flagged when simulated availability is below zero. The buy does not cover the SKU's unfulfilled orders.",
    keyWords: ["availability violation", "warning", "negative availability", "flag", "shortage"],
    group: TableColumnGroups.ExternalData,
  },
  [SupplyTableColumn.ETA]: {
    id: SupplyTableColumn.ETA,
    columnTitle: "ETA",
    columnChooserTitle: "ETA",
    tooltip:
      "Expected time of arrival: the Monday of the week the collection starts. Falls back to the earliest order date plus variant lead time when the collection has no start date.",
    keyWords: ["eta", "ead", "etd", "arrival", "arrival date", "lead time"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.ETD]: {
    id: SupplyTableColumn.ETD,
    columnTitle: "ETD",
    columnChooserTitle: "ETD",
    tooltip:
      "Estimated time of departure. This is the ETA minus the vendor's shipping lead time to that warehouse, or the SKU's transport lead time when the vendor has none set, normalized to the Monday of that week. When the goods have to leave the vendor.",
    keyWords: [
      "etd",
      "estimated time of departure",
      "departure",
      "ex-factory",
      "ship date",
      "production",
    ],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.OPD]: {
    id: SupplyTableColumn.OPD,
    columnTitle: "OPD",
    columnChooserTitle: "OPD",
    tooltip:
      "Order placement date. This is the ETD minus the production lead time, or minus the SKU lead time less the vendor's longest shipping leg when no production lead time is supplied. When the order has to be sent to the vendor.",
    keyWords: ["opd", "order placement date", "order date", "place order", "order by", "lead time"],
    group: TableColumnGroups.SupplyPlanning,
  },
  [SupplyTableColumn.Comments]: {
    id: SupplyTableColumn.Comments,
    columnTitle: "Comments",
    columnChooserTitle: "Comments",
    tooltip: "Comments associated with the product variant.",
    keyWords: ["comments", "notes", "remarks"],
    group: TableColumnGroups.SupplyPlanning,
  },
  // SKU-level FashionBuyTable columns. Display must match FashionBuyTable's headerNames.
  [SupplyTableColumn.Grouping]: {
    id: SupplyTableColumn.Grouping,
    columnTitle: "Variant",
    columnChooserTitle: "Variant",
    tooltip: "The row grouping: the variant, or the dimension picked in Group by.",
    keyWords: ["variant", "grouping", "product"],
    group: TableColumnGroups.ProductInformation,
  },
  [SupplyTableColumn.VariantGrouping]: {
    id: SupplyTableColumn.VariantGrouping,
    columnTitle: "Variant",
    columnChooserTitle: "Variant",
    tooltip:
      "The variant level shown under the chosen grouping, such as each variant of a product.",
    keyWords: ["variant", "grouping", "product"],
    group: TableColumnGroups.ProductInformation,
  },
  [SupplyTableColumn.Sku]: {
    id: SupplyTableColumn.Sku,
    columnTitle: "SKU",
    columnChooserTitle: "SKU",
    tooltip: "The stock-keeping unit (SKU) used as a unique identifier in the system.",
    keyWords: ["product code", "sku", "identifier"],
    group: TableColumnGroups.ProductInformation,
  },
  [SupplyTableColumn.Size]: productColumnNaming[ProductTableColumn.Size],
  [SupplyTableColumn.SupplierSku]: productColumnNaming[ProductTableColumn.SupplierSku],
  [SupplyTableColumn.Tags]: productColumnNaming[ProductTableColumn.Tags],
  [SupplyTableColumn.PreOrders]: collectionColumnNaming[CollectionTableColumn.PreOrders],
  [SupplyTableColumn.IncomingOrdersInPeriod]:
    collectionColumnNaming[CollectionTableColumn.IncomingOrders],
}

/** Demand-insight metric namings. */
const salesMetricColumnNaming: Record<SalesMetricColumns, ColumnNaming> = {
  [SalesMetricColumns.Alerts]: {
    id: SalesMetricColumns.Alerts,
    columnTitle: "Alerts",
    columnChooserTitle: "Alerts",
    tooltip: "Flags potential low-quality matches for review",
    keyWords: ["alerts"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.Type]: {
    id: SalesMetricColumns.Type,
    columnTitle: "Type",
    columnChooserTitle: "Type",
    tooltip:
      "Whether the option was present in the comparison collection (thus carry-over) or it is wasn't (thus new)",
    keyWords: ["baseline type", "carry-over", "new"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.Baseline]: {
    id: SalesMetricColumns.Baseline,
    columnTitle: "Baseline",
    columnChooserTitle: "Baseline",
    tooltip: `The total amount of gross ${formatName("sale", true)} based on historical gross ${formatName("sale", true)} for returning or similar products.`,
    keyWords: ["baseline"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.BaselineMethod]: {
    id: SalesMetricColumns.BaselineMethod,
    columnTitle: "Baseline method",
    columnChooserTitle: "Baseline method",
    tooltip: `The ideal approach used to determine the baseline value.`,
    keyWords: ["baseline method"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.BaselineMatchCount]: {
    id: SalesMetricColumns.BaselineMatchCount,
    columnTitle: "Baseline Match Count",
    columnChooserTitle: "Baseline Match Count",
    tooltip:
      "Number of similar variants used to build the baseline forecast for this variant. Empty for baselines that don't rely on similar variants.",
    keyWords: ["baseline match count", "matches", "neighbors", "custom matches"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.BaselineMatchQuality]: {
    id: SalesMetricColumns.BaselineMatchQuality,
    columnTitle: "Baseline Match Quality",
    columnChooserTitle: "Baseline Match Quality",
    tooltip: "Average similarity of matches used in the baseline demand.",
    keyWords: ["match quality", "similarity", "match score"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.BaselineInfo]: {
    id: SalesMetricColumns.BaselineInfo,
    columnTitle: "Baseline info",
    columnChooserTitle: "Baseline info",
    tooltip:
      "Flags variants whose matched variants live in an unfinished comparison collection, either Upcoming or still Active. Baselines built from them may keep fluctuating. Also flags 'Out' when a matched variant is fully out of the collection it's matched on, so it contributes no baseline.",
    keyWords: ["baseline info"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.Adjustments]: {
    id: SalesMetricColumns.Adjustments,
    columnTitle: "Adjustments",
    columnChooserTitle: "Adjustments",
    tooltip: `All the adjustments coming from historical observations and trends.`,
    keyWords: ["adjustments"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.AdjustmentsPct]: {
    id: SalesMetricColumns.AdjustmentsPct,
    columnTitle: "Adjustments (%)",
    columnChooserTitle: "Adjustments (%)",
    tooltip: `Where the trends and historic adjustments adds the most from the baseline`,
    keyWords: ["adjustments", "forecast", "percentage"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.AddedQuantityStockout]: {
    id: SalesMetricColumns.AddedQuantityStockout,
    columnTitle: "Expected stockouts",
    columnChooserTitle: "Expected stockouts",
    columnSubtitle: "part of adjustments",
    tooltip: `Shows the estimated lost ${formatName("sale", true)} due to stockouts in the comparison collection.`,
    keyWords: ["added quantity stockout", "quantity stockouts", "stockout"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.AddedQuantityMissing]: {
    id: SalesMetricColumns.AddedQuantityMissing,
    columnTitle: "Expected unfinished collection",
    columnChooserTitle: "Expected unfinished collection",
    columnSubtitle: "part of adjustments",
    tooltip: `When the comparison collection is still active, Hakio project the remainder of the collection.`,
    keyWords: ["added quantity missing", "quantity missing", "missing data"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.AddedQuantityDiscount]: {
    id: SalesMetricColumns.AddedQuantityDiscount,
    columnTitle: "Expected discount impact",
    columnChooserTitle: "Expected discount impact",
    columnSubtitle: "part of adjustments",
    tooltip: `Any discounts in the comparison collection will inflate ${formatName("sale", true)} and Hakio are estimating the impact.`,
    keyWords: ["added quantity discount", "quantity discount", "discount data"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.PreorderChange]: {
    id: SalesMetricColumns.PreorderChange,
    columnTitle: `${formatName("preorder", true, true)} change`,
    columnChooserTitle: `${formatName("preorder", true, true)} change`,
    columnSubtitle: "part of adjustments",
    tooltip: `The current collection ${formatName("preorder", true, false)} are not reflected in the baseline. Toggle on to only reflect the current collection ${formatName("preorder", true, false)} into the demand.`,
    keyWords: ["preorder change", "change in preorder", "preorder"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.ExpectedPreorderChange]: {
    id: SalesMetricColumns.ExpectedPreorderChange,
    columnTitle: `Forecasted ${formatName("preorder", true, true)}`,
    columnChooserTitle: `Forecasted ${formatName("preorder", true, true)}`,
    columnSubtitle: "part of adjustments",
    tooltip: `Remaining ${formatName("preorder", true, false)} forecasted based on historical patterns.`,
    keyWords: ["expected preorder", "preorder change", "expected"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.BaselinePreOrders]: {
    id: SalesMetricColumns.BaselinePreOrders,
    columnTitle: `Baseline ${formatName("preorder", true)}`,
    columnChooserTitle: `Baseline ${formatName("preorder", true)}`,
    tooltip: `The amount of ${formatName("preorder", true, false)} present in the baseline`,
    keyWords: ["baseline preorder"],
    group: TableColumnGroups.DemandMetrics,
  },
  // [SalesMetricColumns.CategoryTrend]: {
  //   id: SalesMetricColumns.CategoryTrend,
  //   columnTitle: "Category trend",
  //   columnChooserTitle: "Category trend",
  //   keyWords: ["category trend", "trend in category", "category"],
  //   group: TableColumnGroups.DemandMetrics,
  // },
  // [SalesMetricColumns.ColorTrend]: {
  //   id: SalesMetricColumns.ColorTrend,
  //   columnTitle: "Color trend",
  //   columnChooserTitle: "Color trend",
  //   keyWords: ["color trend", "trend in color", "color"],
  //   group: TableColumnGroups.DemandMetrics,
  // },
  // [SalesMetricColumns.BaseColorTrend]: {
  //   id: SalesMetricColumns.BaseColorTrend,
  //   columnTitle: "Base color trend",
  //   columnChooserTitle: "Base color trend",
  //   keyWords: ["base color trend", "trend in base color", "base color"],
  //   group: TableColumnGroups.DemandMetrics,
  // },
  // [SalesMetricColumns.GenderTrend]: {
  //   id: SalesMetricColumns.GenderTrend,
  //   columnTitle: "Gender trend",
  //   columnChooserTitle: "Gender trend",
  //   keyWords: ["gender trend", "trend in gender", "gender"],
  //   group: TableColumnGroups.DemandMetrics,
  // },
  // [SalesMetricColumns.PreOrderTrend]: {
  //   id: SalesMetricColumns.PreOrderTrend,
  //   columnTitle: `${formatName("preorder", false, true)} trend`,
  //   columnChooserTitle: `${formatName("preorder", false, true)} trend`,
  //   keyWords: ["preorder trend", "trend in preorders", "preorder"],
  //   group: TableColumnGroups.DemandMetrics,
  // },
  [SalesMetricColumns.RiskBuyForecast]: {
    id: SalesMetricColumns.RiskBuyForecast,
    columnTitle: "Risk buy",
    columnChooserTitle: "Risk buy",
    tooltip: `The expected demand excluding confirmed ${formatName("preorder", true)}.`,
    keyWords: ["risk buy forecast", "forecast", "risk buy"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.RiskBuyForecastPct]: {
    id: SalesMetricColumns.RiskBuyForecastPct,
    columnTitle: "Risk buy (%)",
    columnChooserTitle: "Risk buy (%)",
    tooltip: `The share of the expected demand that is risk buy (excluding confirmed ${formatName("preorder", true)}).`,
    keyWords: ["risk buy forecast", "forecast", "risk buy", "percentage"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.RiskBuyForecastComparison]: {
    id: SalesMetricColumns.RiskBuyForecastComparison,
    columnTitle: "Risk buy forecast vs baseline",
    columnChooserTitle: "Risk buy forecast vs baseline",
    tooltip: `The relative amount of risk buy ${formatName("sale", true)} compared to the baseline ${formatName("sale", true)}.`,
    keyWords: ["risk buy forecast", "forecast", "risk buy"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.RiskBuyForecastComparisonPct]: {
    id: SalesMetricColumns.RiskBuyForecastComparisonPct,
    columnTitle: "Risk buy forecast vs baseline (%)",
    columnChooserTitle: "Risk buy forecast vs baseline (%)",
    tooltip: `The relative percentage of risk buy ${formatName("sale", true)} compared to the baseline ${formatName("sale", true)}.`,
    keyWords: ["risk buy forecast", "forecast", "risk buy"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.PreordersVsBaseline]: {
    id: SalesMetricColumns.PreordersVsBaseline,
    columnTitle: `${formatName("preorder", true, true)} vs baseline`,
    columnChooserTitle: `${formatName("preorder", true, true)} vs baseline`,
    tooltip: `The difference between current collection ${formatName("preorder", true, false)} and baseline ${formatName("preorder", true, false)}.`,
    keyWords: ["preorders", "baseline", "preorders vs baseline"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.PreordersVsBaselinePct]: {
    id: SalesMetricColumns.PreordersVsBaselinePct,
    columnTitle: `${formatName("preorder", true, true)} vs baseline (%)`,
    columnChooserTitle: `${formatName("preorder", true, true)} vs baseline (%)`,
    tooltip: `The percentage difference between current collection ${formatName("preorder", true, false)} and baseline ${formatName("preorder", true, false)}.`,
    keyWords: ["preorders", "baseline", "preorders vs baseline", "percentage"],
    group: TableColumnGroups.DemandMetrics,
  },
  [SalesMetricColumns.FinalForecast]: {
    id: SalesMetricColumns.FinalForecast,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    tooltip: `The expected demand includes known ${formatName("preorder", true)}, forecasted ${formatName("preorder", true)}, and forecasted reorders.`,
    keyWords: ["final forecast", "total forecast", "forecast"],
    group: TableColumnGroups.DemandMetrics,
  },
}

/** Trim-metric namings. */
const trimMetricColumnNaming: Record<TrimMetricsColumns, ColumnNaming> = {
  [TrimMetricsColumns.PreorderQuantity]: {
    id: TrimMetricsColumns.PreorderQuantity,
    columnTitle: formatName("preorder", true, true),
    columnChooserTitle: `${formatName("preorder", true, true)} quantity`,
    tooltip: `The total amount of ${formatName("preorder", true)} across all variants in the plan.`,
    keyWords: [
      "future sales",
      "preorders",
      "pre-orders",
      "pre orders",
      "future preorders",
      "future pre-orders",
      formatName("preorder", true),
      formatName("preorder"),
    ],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.ExpectedPreorders]: {
    id: TrimMetricsColumns.ExpectedPreorders,
    columnTitle: `Forecasted ${formatName("preorder", true)}`,
    columnChooserTitle: `Forecasted ${formatName("preorder", true)}`,
    tooltip: `The ${formatName("preorder", true)} forecasted to be placed for the product.`,
    keyWords: ["expected preorders", "anticipated preorders", "preorders"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.CustomerInterest]: {
    id: TrimMetricsColumns.CustomerInterest,
    columnTitle: "Customer interest",
    columnChooserTitle: "Customer interest",
    tooltip:
      // eslint-disable-next-line @hakio/no-untranslated-terminology
      "The overall assessment of customer interest. Calculated as the average rating of customer count, customer coverage and sales-orders per customer.",
    keyWords: ["customer interest", "customer engagement", "customer demand"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.Cannibalisation]: {
    id: TrimMetricsColumns.Cannibalisation,
    columnTitle: "Cannibalisation",
    columnChooserTitle: "Cannibalisation",
    tooltip:
      "The overall assessment of cannibalisation. Calculated as the average rating of quality and number of neighboring products.",
    keyWords: ["cannibalisation", "product competition", "sales impact"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.SourcingRisk]: {
    id: TrimMetricsColumns.SourcingRisk,
    columnTitle: "Sourcing risk",
    columnChooserTitle: "Sourcing risk",
    tooltip: `An indication whether sourcing the ${formatName("preorder", true)} and risk buy ${formatName("sale", true)} might be problematic.`,
    keyWords: ["sourcing risk", "supply risk", "procurement risk"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.TrimAction]: {
    id: TrimMetricsColumns.TrimAction,
    columnTitle: "Action",
    columnChooserTitle: "Trim action",
    tooltip: `Remove or add back variants.`,
    keyWords: ["trim action", "variant action", "product variant"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.CustomerCount]: {
    id: TrimMetricsColumns.CustomerCount,
    columnTitle: "Customer count",
    columnChooserTitle: "Customer count",
    tooltip: `The amount of unique customers who have placed an order.`,
    keyWords: ["Customer count", "unique customers", "number of customers"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.CustomerCoverage]: {
    id: TrimMetricsColumns.CustomerCoverage,
    columnTitle: "Customer coverage",
    columnChooserTitle: "Customer coverage",
    tooltip:
      "Share of units sold in the baseline collections that came from customers who ordered this variant.",
    keyWords: ["customer coverage", "units share", "customer share"],
    group: TableColumnGroups.TrimMetrics,
  },
  [TrimMetricsColumns.AvgQuantityPerCustomer]: {
    id: TrimMetricsColumns.AvgQuantityPerCustomer,
    columnTitle: "Avg quantity per customer",
    columnChooserTitle: "Avg quantity per customer",
    tooltip: `Average units sold per customer in the plan's source collections. Counts customers who bought or placed a ${formatName("preorder")}.`,
    keyWords: ["average quantity per customer"],
    group: TableColumnGroups.TrimMetrics,
  },
}

/**
 * All column namings gathered in one lookup. Update labels, tooltips or search keywords
/** Budget variant table namings. */
const budgetVariantColumnNaming: Record<BudgetVariantColumn, ColumnNaming> = {
  [BudgetVariantColumn.BudgetForecast]: {
    id: BudgetVariantColumn.BudgetForecast,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    columnSubtitle: "Units",
    tooltip:
      "Expected demand in units for the budget period from the current week. Limited to collection date ranges when applicable.",
  },
  [BudgetVariantColumn.ForecastedRevenue]: {
    id: BudgetVariantColumn.ForecastedRevenue,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    tooltip:
      "Expected demand in currency: demand units times the unit price from the budget's calculation method. Covers the budget period from the current week.",
  },
  [BudgetVariantColumn.AllocatedBudgetUnits]: {
    id: BudgetVariantColumn.AllocatedBudgetUnits,
    columnTitle: "Target",
    columnChooserTitle: "Target",
    columnSubtitle: "Units",
    tooltip:
      "The budget target split across variants by their share of expected demand, in units. Covers the budget period from the current week.",
  },
  [BudgetVariantColumn.AllocatedBudgetCurrency]: {
    id: BudgetVariantColumn.AllocatedBudgetCurrency,
    columnTitle: "Target",
    columnChooserTitle: "Target",
    tooltip:
      "The budget target split across variants by their share of expected demand, in currency. Covers the budget period from the current week.",
  },
  [BudgetVariantColumn.VarianceUnits]: {
    id: BudgetVariantColumn.VarianceUnits,
    columnTitle: "Variance",
    columnChooserTitle: "Variance",
    columnSubtitle: "Units",
    tooltip: "Target minus demand, in units. Positive means demand is below target.",
  },
  [BudgetVariantColumn.VarianceCurrency]: {
    id: BudgetVariantColumn.VarianceCurrency,
    columnTitle: "Variance",
    columnChooserTitle: "Variance",
    tooltip: "Target minus demand, in currency. Positive means demand is below target.",
  },
}

/** Adjustment manager dialog table namings. */
const adjustmentManagerColumnNaming: Record<AdjustmentManagerColumn, ColumnNaming> = {
  [AdjustmentManagerColumn.Theme]: {
    id: AdjustmentManagerColumn.Theme,
    columnTitle: "Theme",
    columnChooserTitle: "Theme",
  },
  [AdjustmentManagerColumn.Warehouse]: {
    id: AdjustmentManagerColumn.Warehouse,
    columnTitle: "Warehouse",
    columnChooserTitle: "Warehouse",
  },
  [AdjustmentManagerColumn.Channels]: {
    id: AdjustmentManagerColumn.Channels,
    columnTitle: "Channels",
    columnChooserTitle: "Channels",
  },
  [AdjustmentManagerColumn.Value]: {
    id: AdjustmentManagerColumn.Value,
    columnTitle: "Value",
    columnChooserTitle: "Value",
  },
  [AdjustmentManagerColumn.VariantCount]: {
    id: AdjustmentManagerColumn.VariantCount,
    columnTitle: "Variants",
    columnChooserTitle: "Variants",
  },
  [AdjustmentManagerColumn.Period]: {
    id: AdjustmentManagerColumn.Period,
    columnTitle: "Effective period",
    columnChooserTitle: "Effective period",
  },
  [AdjustmentManagerColumn.UpdatedBy]: {
    id: AdjustmentManagerColumn.UpdatedBy,
    columnTitle: "Updated by",
    columnChooserTitle: "Updated by",
  },
}

/** NPI match dialog (forecast) table namings. */
const npiDialogMatchColumnNaming: Record<NpiDialogMatchColumn, ColumnNaming> = {
  [NpiDialogMatchColumn.MatchScore]: {
    id: NpiDialogMatchColumn.MatchScore,
    columnTitle: "Match Score",
    columnChooserTitle: "Match Score",
    tooltip: "Similarity between this variant and the new product. Higher is a closer match.",
  },
  // Subtitle and chooser label are overridden at runtime to the picked demand basis; the values
  // here are the defaults (basis defaults to "firstYear").
  [NpiDialogMatchColumn.BasisSales]: {
    id: NpiDialogMatchColumn.BasisSales,
    columnTitle: "Demand basis",
    columnSubtitle: "First 52 weeks",
    columnChooserTitle: "Demand basis (First 52 weeks)",
    tooltip:
      "Units sold in the window set by the match's demand basis type. Matches not picked yet show their first 52 weeks.",
    keyWords: ["sales", "demand basis", "first 52 weeks", "last 52 weeks", "yearly average"],
  },
  [NpiDialogMatchColumn.DemandBasis]: {
    id: NpiDialogMatchColumn.DemandBasis,
    columnTitle: "Demand basis type",
    columnChooserTitle: "Demand basis type",
    tooltip: `Which window of the match's history feeds the forecast: first 52 weeks, last 52 weeks, a yearly average, or one specific year.`,
    keyWords: [
      "demand basis",
      "demand basis type",
      "forecast basis",
      "sales window",
      "first 52 weeks",
      "last 52 weeks",
    ],
  },
}

/** Demand-plan NPI collection-match dialog table namings. */
const demandNpiMatchColumnNaming: Record<DemandNpiMatchColumn, ColumnNaming> = {
  [DemandNpiMatchColumn.Collections]: {
    id: DemandNpiMatchColumn.Collections,
    columnTitle: "Collections",
    columnChooserTitle: "Collections",
    tooltip:
      "Collections the variant has sold in, excluding NOOS collections and the new product's own collections.",
  },
  [DemandNpiMatchColumn.CollectionSales]: {
    id: DemandNpiMatchColumn.CollectionSales,
    columnTitle: `Collection ${formatName("sale", true, true)}`,
    columnChooserTitle: `Collection ${formatName("sale", true, true)}`,
    tooltip: `Units sold in the variant's best-selling collection, over that collection's ${formatName("sale", true)} period. This collection becomes the match's basis when added.`,
  },
  [DemandNpiMatchColumn.NoosSales]: {
    id: DemandNpiMatchColumn.NoosSales,
    columnTitle: `First year ${formatName("sale", true)}`,
    columnChooserTitle: `First year ${formatName("sale", true)}`,
    tooltip: `Units sold in the first 52 weeks after launch. Each match's basis can be changed once it is added.`,
  },
  [DemandNpiMatchColumn.NoosLastYearSales]: {
    id: DemandNpiMatchColumn.NoosLastYearSales,
    columnTitle: `Last 52 weeks ${formatName("sale", true)}`,
    columnChooserTitle: `Last 52 weeks ${formatName("sale", true)}`,
    tooltip: `Units sold in the last 52 weeks.`,
  },
  [DemandNpiMatchColumn.MatchScore]: npiDialogMatchColumnNaming[NpiDialogMatchColumn.MatchScore],
}

/** Market Strategy channel list namings. */
const marketStrategyColumnNaming: Record<MarketStrategyColumn, ColumnNaming> = {
  [MarketStrategyColumn.Channel]: {
    id: MarketStrategyColumn.Channel,
    columnTitle: "Channel",
    columnChooserTitle: "Channel",
  },
  [MarketStrategyColumn.ChannelType]: {
    id: MarketStrategyColumn.ChannelType,
    columnTitle: "Type",
    columnChooserTitle: "Channel type",
  },
  [MarketStrategyColumn.WhereItSells]: {
    id: MarketStrategyColumn.WhereItSells,
    columnTitle: "Where it sells",
    columnChooserTitle: "Where it sells",
    columnSubtitle: "In scope",
    tooltip:
      "Warehouse and market combinations still in planning scope for this channel, out of those available.",
  },
  [MarketStrategyColumn.Sales]: {
    id: MarketStrategyColumn.Sales,
    columnTitle: formatName("sale", true, true),
    columnChooserTitle: `${formatName("sale", true, true)} (Last 52 weeks)`,
    columnSubtitle: "Last 52 weeks",
  },
}

/** Forecast evaluation table namings. */
const forecastEvaluationColumnNaming: Record<ForecastEvaluationColumn, ColumnNaming> = {
  [ForecastEvaluationColumn.ForecastQuality]:
    productColumnNaming[ProductTableColumn.ForecastQuality],
  [ForecastEvaluationColumn.Forecast]: {
    id: ForecastEvaluationColumn.Forecast,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    columnSubtitle: "Hakio",
    tooltip:
      "Units Hakio forecast for the evaluation period. The range picked above the table sets how long ago the forecast was made.",
    group: TableColumnGroups.SalesAndForecast,
  },
  [ForecastEvaluationColumn.Sales]: {
    id: ForecastEvaluationColumn.Sales,
    columnTitle: `Actual ${formatName("sale", true)}`,
    columnChooserTitle: `Actual ${formatName("sale", true)}`,
    tooltip:
      "Units sold in the evaluation period, the same period Hakio and the baseline forecast.",
  },
  [ForecastEvaluationColumn.Difference]: {
    id: ForecastEvaluationColumn.Difference,
    columnTitle: "Difference",
    columnChooserTitle: "Difference",
    columnSubtitle: "Hakio",
    tooltip: `Hakio demand minus actual ${formatName("sale", true)}, in units. Positive means Hakio forecast too high.`,
  },
  [ForecastEvaluationColumn.DifferenceBaseline]: {
    id: ForecastEvaluationColumn.DifferenceBaseline,
    columnTitle: "Difference",
    columnChooserTitle: "Difference",
    columnSubtitle: "Baseline",
    tooltip: `Baseline demand minus actual ${formatName("sale", true)}, in units. Positive means the baseline forecast too high.`,
  },
  [ForecastEvaluationColumn.DifferencePercentage]: {
    id: ForecastEvaluationColumn.DifferencePercentage,
    columnTitle: "Difference %",
    columnChooserTitle: "Difference %",
    columnSubtitle: "Hakio",
    tooltip: `Hakio demand minus actual ${formatName("sale", true)}, as a share of actual ${formatName("sale", true)}. N/A when either is 0.`,
  },
  [ForecastEvaluationColumn.DifferenceBaselinePercentage]: {
    id: ForecastEvaluationColumn.DifferenceBaselinePercentage,
    columnTitle: "Difference %",
    columnChooserTitle: "Difference %",
    columnSubtitle: "Baseline",
    tooltip: `Baseline demand minus actual ${formatName("sale", true)}, as a share of actual ${formatName("sale", true)}. N/A when either is 0.`,
  },
  [ForecastEvaluationColumn.Baseline]: {
    id: ForecastEvaluationColumn.Baseline,
    columnTitle: "Demand",
    columnChooserTitle: "Demand",
    columnSubtitle: "Baseline",
    tooltip:
      "Units the baseline model picked above the table forecast for the same evaluation period as Hakio.",
  },
  [ForecastEvaluationColumn.AbsoluteError]: {
    id: ForecastEvaluationColumn.AbsoluteError,
    columnTitle: "Absolute Error",
    columnChooserTitle: "Absolute Error",
    columnSubtitle: "Hakio",
    tooltip: `Gap between Hakio demand and actual ${formatName("sale", true)} in units, in either direction. Lower is better.`,
  },
  [ForecastEvaluationColumn.BaselineAbsoluteError]: {
    id: ForecastEvaluationColumn.BaselineAbsoluteError,
    columnTitle: "Absolute Error",
    columnChooserTitle: "Absolute Error",
    columnSubtitle: "Baseline",
    tooltip: `Gap between baseline demand and actual ${formatName("sale", true)} in units, in either direction. Lower is better.`,
  },
  [ForecastEvaluationColumn.AbsoluteErrorPercentage]: {
    id: ForecastEvaluationColumn.AbsoluteErrorPercentage,
    columnTitle: "Absolute Error %",
    columnChooserTitle: "Absolute Error %",
    columnSubtitle: "Hakio",
    tooltip: `Hakio's absolute error as a share of actual ${formatName("sale", true)}. Lower is better. N/A when either is 0.`,
  },
  [ForecastEvaluationColumn.BaselineAbsoluteErrorPercentage]: {
    id: ForecastEvaluationColumn.BaselineAbsoluteErrorPercentage,
    columnTitle: "Absolute Error %",
    columnChooserTitle: "Absolute Error %",
    columnSubtitle: "Baseline",
    tooltip: `The baseline's absolute error as a share of actual ${formatName("sale", true)}. Lower is better. N/A when either is 0.`,
  },
  [ForecastEvaluationColumn.OverstockImprovement]: {
    id: ForecastEvaluationColumn.OverstockImprovement,
    columnTitle: "Overstock improvement",
    columnChooserTitle: "Overstock improvement",
    tooltip:
      "Shows the difference in overstock between Hakio and Baseline. A positive number means less inventory. Safety stock is not accounted for.",
  },
  [ForecastEvaluationColumn.UnderstockImprovement]: {
    id: ForecastEvaluationColumn.UnderstockImprovement,
    columnTitle: "Understock improvement",
    columnChooserTitle: "Understock improvement",
    tooltip: `Shows the difference in understock between Hakio and Baseline. A positive number means more ${formatName("sale", true)}. Safety stock is not accounted for.`,
  },
  [ForecastEvaluationColumn.Overstock]: {
    id: ForecastEvaluationColumn.Overstock,
    columnTitle: "Overstock",
    columnChooserTitle: "Overstock",
    columnSubtitle: "Hakio",
    tooltip: `Units Hakio forecast above actual ${formatName("sale", true)}. 0 when ${formatName("sale", true)} were higher.`,
  },
  [ForecastEvaluationColumn.BaselineOverstock]: {
    id: ForecastEvaluationColumn.BaselineOverstock,
    columnTitle: "Overstock",
    columnChooserTitle: "Overstock",
    columnSubtitle: "Baseline",
    tooltip: `Units the baseline forecast above actual ${formatName("sale", true)}. 0 when ${formatName("sale", true)} were higher.`,
  },
  [ForecastEvaluationColumn.Understock]: {
    id: ForecastEvaluationColumn.Understock,
    columnTitle: "Understock",
    columnChooserTitle: "Understock",
    columnSubtitle: "Hakio",
    tooltip: `Units actual ${formatName("sale", true)} exceeded Hakio demand. 0 when demand was higher.`,
  },
  [ForecastEvaluationColumn.BaselineUnderstock]: {
    id: ForecastEvaluationColumn.BaselineUnderstock,
    columnTitle: "Understock",
    columnChooserTitle: "Understock",
    columnSubtitle: "Baseline",
    tooltip: `Units actual ${formatName("sale", true)} exceeded baseline demand. 0 when demand was higher.`,
  },
}

/** NOOS order suggestions card column namings. */
const noosOrderSuggestionsColumnNaming: Record<NoosOrderSuggestionsColumn, ColumnNaming> = {
  [NoosOrderSuggestionsColumn.OrderStatus]: {
    id: NoosOrderSuggestionsColumn.OrderStatus,
    columnTitle: "Action needed",
    columnChooserTitle: "Action needed",
    tooltip: "Indicates whether a buy needs to be created to avoid a stockout.",
    keyWords: ["action needed", "order status", "create buy", "urgent"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [NoosOrderSuggestionsColumn.SuggestedQty]: {
    id: NoosOrderSuggestionsColumn.SuggestedQty,
    columnTitle: SUGGESTED_ORDER_TITLE,
    columnChooserTitle: SUGGESTED_ORDER_TITLE,
    tooltip: SUGGESTED_ORDER_TOOLTIP,
    keyWords: ["suggested order", "suggested quantity", "minimum required", "reorder"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [NoosOrderSuggestionsColumn.MoqSuggestedQty]: {
    id: NoosOrderSuggestionsColumn.MoqSuggestedQty,
    columnTitle: MOQ_SUGGESTED_ORDER_TITLE,
    columnChooserTitle: MOQ_SUGGESTED_ORDER_TITLE,
    tooltip: MOQ_SUGGESTED_ORDER_TOOLTIP,
    keyWords: ["moq", "moq suggestion", "minimum order quantity", "suggested order"],
    group: TableColumnGroups.OperationalMetrics,
  },
  [NoosOrderSuggestionsColumn.TriggerReasons]: {
    id: NoosOrderSuggestionsColumn.TriggerReasons,
    columnTitle: "Trigger",
    columnChooserTitle: "Trigger",
    tooltip: `Why the variant is triggered: stockout before delivery, delayed PO, below safety stock or high lost ${formatName("sale", true)}.`,
    keyWords: ["reason", "trigger", "alert", "why"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [NoosOrderSuggestionsColumn.TriggerStockout]: {
    id: NoosOrderSuggestionsColumn.TriggerStockout,
    columnTitle: "Stockout",
    columnChooserTitle: "Stockout period",
    tooltip:
      "When the first SKU runs out (worst case across SKUs and warehouses) → when anything can land: the delayed PO, or else an order placed today.",
    keyWords: ["stockout", "gap", "runs out", "cover"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [NoosOrderSuggestionsColumn.TriggerImpact]: {
    id: NoosOrderSuggestionsColumn.TriggerImpact,
    columnTitle: "Impact",
    columnChooserTitle: "Impact",
    tooltip: `Forecast units lost within lead time: expected ${formatName("sale", true)} the projected stock can't cover.`,
    keyWords: ["impact", "lost", "units lost"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [NoosOrderSuggestionsColumn.OrderByDate]: {
    id: NoosOrderSuggestionsColumn.OrderByDate,
    columnTitle: "Order by",
    columnChooserTitle: "Order by",
    tooltip:
      "Latest order date that still lands before the first SKU runs out: the stockout date minus lead time. Overdue when a SKU is already out.",
    keyWords: ["order by date", "due date", "order deadline", "latest order date"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
}

/**
 * All centrally-named columns gathered in one lookup. Update labels, tooltips or search keywords
 * in the per-group record above — this combines them so consumers have a single source of truth
 * keyed by any {@link NamedColumnId}.
 */
/** Purchase order table namings. */
export const purchaseOrderColumnNaming: Record<PurchaseOrderTableColumn, ColumnNaming> = {
  [PurchaseOrderTableColumn.Grouping]: supplyColumnNaming[SupplyTableColumn.Grouping],
  [PurchaseOrderTableColumn.SecondaryMoqGrouper]: {
    id: PurchaseOrderTableColumn.SecondaryMoqGrouper,
    columnTitle: "Secondary MOQ group",
    columnChooserTitle: "Secondary MOQ group",
    tooltip:
      "The group the secondary MOQ applies to, set in company settings. The variant by default.",
    group: TableColumnGroups.ProductInformation,
  },
  [PurchaseOrderTableColumn.SKU]: supplyColumnNaming[SupplyTableColumn.Sku],
  [PurchaseOrderTableColumn.Comments]: supplyColumnNaming[SupplyTableColumn.Comments],
  [PurchaseOrderTableColumn.Quantity]: {
    id: PurchaseOrderTableColumn.Quantity,
    columnTitle: "Quantity",
    columnChooserTitle: "Quantity",
    tooltip: "The quantity to order for this SKU.",
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.SuggestedOrder]: {
    id: PurchaseOrderTableColumn.SuggestedOrder,
    columnTitle: SUGGESTED_ORDER_TITLE,
    columnChooserTitle: SUGGESTED_ORDER_TITLE,
    tooltip: SUGGESTED_ORDER_TOOLTIP,
    keyWords: ["suggested order", "recommended quantity", "suggestion", "minimum required"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.MoqSuggestedOrder]: {
    id: PurchaseOrderTableColumn.MoqSuggestedOrder,
    columnTitle: MOQ_SUGGESTED_ORDER_TITLE,
    columnChooserTitle: MOQ_SUGGESTED_ORDER_TITLE,
    tooltip: MOQ_SUGGESTED_ORDER_TOOLTIP,
    keyWords: ["moq", "moq suggestion", "minimum order quantity", "suggested order"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.SecondaryMOQ]: {
    id: PurchaseOrderTableColumn.SecondaryMOQ,
    columnTitle: secondaryMoqName,
    columnChooserTitle: secondaryMoqName,
    tooltip: "Secondary minimum order quantity for grouped SKUs.",
    keyWords: ["secondary moq", "group moq", "secondary minimum order"],
    group: TableColumnGroups.MOQAndConstraints,
  },
  [PurchaseOrderTableColumn.Forecast]: {
    id: PurchaseOrderTableColumn.Forecast,
    columnTitle: "Demand",
    columnSubtitle: "in period",
    columnChooserTitle: "Demand (In period)",
    tooltip: "The expected demand during the purchase period.",
    keyWords: ["demand", "forecast", "predicted sales", "projection"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.ForecastWithoutPreOrders]: {
    id: PurchaseOrderTableColumn.ForecastWithoutPreOrders,
    columnTitle: "Forecasted reorders",
    columnSubtitle: "in period",
    columnChooserTitle: "Forecasted reorders (In period)",
    tooltip: `The expected demand during the purchase period, excluding both known and forecasted ${formatName("preorder", true)}.`,
    keyWords: [
      "forecasted reorders",
      "reorders",
      "forecast",
      "predicted sales",
      "projection",
      "without preorders",
    ],
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.PreOrders]: {
    id: PurchaseOrderTableColumn.PreOrders,
    columnTitle: formatName("preorder", true, true),
    columnSubtitle: "in period",
    columnChooserTitle: `${formatName("preorder", false, true)} (In period)`,
    tooltip: `Amount of ${formatName("preorder", true)} during the purchase period`,
    keyWords: ["preorders", "pre-orders", "future sales"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.ProjectedWeeksOnHand]: {
    id: PurchaseOrderTableColumn.ProjectedWeeksOnHand,
    columnTitle: "Projected weeks on hand",
    columnChooserTitle: "Projected weeks on hand",
    tooltip: "The projected number of weeks the stock will last after the purchase period.",
    keyWords: ["weeks on hand", "stock duration", "projected weeks"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.MonthCover]: {
    id: PurchaseOrderTableColumn.MonthCover,
    columnTitle: "Month cover",
    columnChooserTitle: "Month cover",
    tooltip: `${formatName("available_quantity", false, true)} divided by monthly ${formatName("sale", true)} (L12M / 12). Falls back to current stock when unavailable.`,
    keyWords: ["month cover", "mc", "coverage", "stock duration"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.MonthCoverWithPO]: {
    id: PurchaseOrderTableColumn.MonthCoverWithPO,
    columnTitle: "Month cover w. PO",
    columnChooserTitle: "Month cover (with PO)",
    tooltip: `Stock at PO arrival incl. this order, divided by monthly ${formatName("sale", true)} (L12M / 12). Demand until arrival is the monthly L12M average × months until arrival.`,
    keyWords: ["month cover", "po", "purchase order", "coverage"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.MonthCoverWithPOMinusLeadtime]: {
    id: PurchaseOrderTableColumn.MonthCoverWithPOMinusLeadtime,
    columnTitle: "Month cover w. PO - LT",
    columnChooserTitle: "Month cover (with PO, minus leadtime)",
    tooltip: "Month cover with PO minus the leadtime (in months).",
    keyWords: ["month cover", "po", "leadtime", "lt", "coverage"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.ProjectedStock]: {
    id: PurchaseOrderTableColumn.ProjectedStock,
    columnTitle: "Projected stock",
    columnSubtitle: "End of purchase period",
    columnChooserTitle: "Projected stock (End of purchase period)",
    tooltip: "The projected stock level at the end of the purchase period.",
    keyWords: ["projected stock", "end stock", "future stock"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.ProjectedStockEAD]: {
    id: PurchaseOrderTableColumn.ProjectedStockEAD,
    columnTitle: "Projected stock",
    columnSubtitle: "ETA",
    columnChooserTitle: "Projected stock (ETA)",
    tooltip: "The stock level at the ETA (estimated time of arrival).",
    keyWords: ["starting stock", "initial stock", "beginning stock", "stock expected arrival date"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.IncomingStockBeforeEAD]: {
    id: PurchaseOrderTableColumn.IncomingStockBeforeEAD,
    columnTitle: "Incoming stock",
    columnSubtitle: "Before ETA",
    columnChooserTitle: "Incoming stock (Before ETA)",
    tooltip:
      "Units on incoming orders expected to arrive before this order's ETA, including overdue orders.",
    keyWords: ["incoming stock", "before eta", "before ead", "pre period", "early stock"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.IncomingStockPostPeriod]: {
    id: PurchaseOrderTableColumn.IncomingStockPostPeriod,
    columnTitle: "Incoming stock",
    columnSubtitle: "Post period",
    columnChooserTitle: "Incoming stock (Post period)",
    tooltip: "Quantity of stock arriving after the End Purchase Order date.",
    keyWords: ["incoming stock", "post period", "late stock", "future stock"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.SizeSplit]: {
    id: PurchaseOrderTableColumn.SizeSplit,
    columnTitle: "Size Split",
    columnChooserTitle: "Size Split",
    tooltip: `This size's share of the variant's expected demand, based on historic ${formatName("sale", true)} (or similar products for new items). Used to split the variant forecast across sizes.`,
    keyWords: ["size split", "size distribution", "size share", "sku split"],
    group: TableColumnGroups.ProductInformation,
  },
  [PurchaseOrderTableColumn.TotalPrice]: {
    id: PurchaseOrderTableColumn.TotalPrice,
    columnTitle: "Total price",
    columnChooserTitle: "Total price",
    tooltip: "The total cost for the ordered quantity.",
    keyWords: ["total price", "total cost", "line total"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.VendorCurrency]: {
    id: PurchaseOrderTableColumn.VendorCurrency,
    columnTitle: "Currency (VC)",
    columnChooserTitle: "Currency (VC)",
    tooltip:
      "VC is the vendor currency, the currency this vendor is paid in. It can differ from your company currency. Shows Mixed when a row's warehouses are supplied by vendors on different currencies.",
    keyWords: ["currency", "vendor currency", "vc", "buy currency"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.PurchasePriceVC]: {
    id: PurchaseOrderTableColumn.PurchasePriceVC,
    columnTitle: "Purchase price (VC)",
    columnChooserTitle: "Purchase price (VC)",
    tooltip:
      "The negotiated unit price in the vendor currency (VC), the currency this vendor is paid in. Edit it here and it carries to the purchase order. Falls back to the vendor's price for the SKU, then the cost price.",
    keyWords: ["purchase price", "vendor price", "unit price", "vc", "factory price"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.TotalPurchasePriceVC]: {
    id: PurchaseOrderTableColumn.TotalPurchasePriceVC,
    columnTitle: "Total purchase price (VC)",
    columnChooserTitle: "Total purchase price (VC)",
    tooltip:
      "Purchase price multiplied by the ordered quantity, in the vendor currency (VC). Blank when a row's warehouses use different currencies.",
    keyWords: ["total purchase price", "total cost", "total price", "vc", "vendor currency"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.TotalPurchasePriceLC]: {
    id: PurchaseOrderTableColumn.TotalPurchasePriceLC,
    columnTitle: "Total purchase price (LC)",
    columnChooserTitle: "Total purchase price (LC)",
    tooltip:
      "The line's total purchase price converted to your company currency (LC) at today's exchange rate. An internal estimate for sense-checking spend, it is never sent to your ERP. Blank when no rate is available for the vendor currency.",
    keyWords: [
      "total purchase price",
      "total cost",
      "local currency",
      "lc",
      "company currency",
      "converted",
    ],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.SafetyStock]: {
    id: PurchaseOrderTableColumn.SafetyStock,
    columnTitle: "Safety stock",
    columnChooserTitle: "Safety stock",
    tooltip: "The safety stock held to cover unforeseen demand spikes.",
    keyWords: ["safety stock", "buffer", "reserve stock"],
    group: TableColumnGroups.OrderQuantities,
  },
  [PurchaseOrderTableColumn.LocationMinimumStockLevel]: {
    id: PurchaseOrderTableColumn.LocationMinimumStockLevel,
    columnTitle: "Location minimum stock",
    columnChooserTitle: "Location minimum stock",
    tooltip: "Minimum stock level on storage locations, currently sendable by ERP-systems",
    keyWords: ["location stock", "location availability"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  [PurchaseOrderTableColumn.Actions]: {
    id: PurchaseOrderTableColumn.Actions,
    columnTitle: "Actions",
    columnChooserTitle: "Actions",
    tooltip: "Actions available for this row.",
    keyWords: ["actions", "operations", "controls"],
    group: TableColumnGroups.Actions,
  },
  [PurchaseOrderTableColumn.PreOrdersBeforeEAD]: {
    id: PurchaseOrderTableColumn.PreOrdersBeforeEAD,
    columnTitle: formatName("preorder", true, true),
    columnSubtitle: "Before ETA",
    columnChooserTitle: `${formatName("preorder", true, true)} (Before ETA)`,
    tooltip: `Units of ${formatName("preorder", true)} with a delivery date from today to before this order's ETA.`,
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.UnfulfillableBeforeEAD]: {
    id: PurchaseOrderTableColumn.UnfulfillableBeforeEAD,
    columnTitle: formatName("unfulfillable_preorder", true, true),
    columnSubtitle: "Before ETA",
    columnChooserTitle: `${formatName("unfulfillable_preorder", true, true)} (Before ETA)`,
    tooltip: `${getUnfulfillableDescriptionShort()} Counts ${formatName("preorder", true)} delivered before the ETA.`,
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.AtRiskBeforeEAD]: {
    id: PurchaseOrderTableColumn.AtRiskBeforeEAD,
    columnTitle: `${formatName("at_risk_preorder", true, true)}`,
    columnSubtitle: "Before ETA",
    columnChooserTitle: `${formatName("at_risk_preorder", true, true)} (Before ETA)`,
    tooltip: `${getAtRiskDescriptionShort()} Counts ${formatName("preorder", true)} delivered before the ETA.`,
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.ProjectedLostSalesInPeriod]: {
    id: PurchaseOrderTableColumn.ProjectedLostSalesInPeriod,
    columnTitle: `Projected lost ${formatName("sale", true)}`,
    columnSubtitle: "in period",
    columnChooserTitle: `Projected lost ${formatName("sale", true)} (In period)`,
    tooltip: `Expected unrealized gross ${formatName("sale", true)} during the purchase period if nothing additional is ordered.`,
    keyWords: ["lost sales", "projected lost sales", "stockout", "purchase period"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.AdjustedLostSalesInPeriod]: {
    id: PurchaseOrderTableColumn.AdjustedLostSalesInPeriod,
    columnTitle: `Adjusted lost ${formatName("sale", true)}`,
    columnSubtitle: "in period",
    columnChooserTitle: `Adjusted lost ${formatName("sale", true)} (In period)`,
    tooltip: `Projected lost ${formatName("sale", true)} minus the quantity on this order: remaining unrealized gross ${formatName("sale", true)} after this purchase.`,
    keyWords: ["adjusted lost sales", "remaining lost sales", "order offset", "purchase period"],
    group: TableColumnGroups.SalesAndForecast,
  },
  [PurchaseOrderTableColumn.ImpactWoH]: {
    id: PurchaseOrderTableColumn.ImpactWoH,
    columnTitle: "Impact weeks on hand",
    columnChooserTitle: "Impact weeks on hand",
    group: TableColumnGroups.AvailabilityAndInventory,
  },
}

const buyColumnNaming: Record<BuySpecificColumn, ColumnNaming> = {
  [BuySpecificColumn.VariantGrouping]: supplyColumnNaming[SupplyTableColumn.VariantGrouping],
  [BuySpecificColumn.TotalQty]: {
    id: BuySpecificColumn.TotalQty,
    columnTitle: "Total qty",
    columnChooserTitle: "Total qty",
    tooltip: "The total quantity to buy for this SKU across warehouses.",
    keyWords: ["quantity", "total", "buy quantity"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.SuggestedOrderDelta]: {
    id: BuySpecificColumn.SuggestedOrderDelta,
    columnTitle: SUGGESTED_ORDER_DELTA_TITLE,
    columnSubtitle: SUGGESTED_ORDER_DELTA_SUBTITLE,
    columnChooserTitle: SUGGESTED_ORDER_DELTA_CHOOSER_TITLE,
    tooltip: suggestedOrderDeltaTooltip("Your buy quantity"),
    keyWords: SUGGESTED_ORDER_DELTA_KEYWORDS,
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.ETA]: {
    id: BuySpecificColumn.ETA,
    columnTitle: "ETA",
    columnChooserTitle: "ETA",
    tooltip:
      "Estimated time of arrival (ETA) taken from the purchase orders attached to this row, per warehouse.",
    keyWords: ["eta", "ead", "expected arrival", "arrival date", "delivery date"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.ETD]: {
    id: BuySpecificColumn.ETD,
    columnTitle: "ETD",
    columnChooserTitle: "ETD",
    tooltip:
      "Estimated time of departure: the ETA minus the vendor's shipping lead time for the warehouse (Monday-normalized).",
    keyWords: ["etd", "estimated time of departure", "ship date", "departure"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.Covers]: {
    id: BuySpecificColumn.Covers,
    columnTitle: "Covers",
    columnChooserTitle: "Covers",
    tooltip:
      "The period this buy is covering for the variant: from its ETA to the end of the buy's purchase period.",
    keyWords: ["covers", "period", "coverage", "buy period", "purchase period", "in period"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.Vendor]: {
    id: BuySpecificColumn.Vendor,
    columnTitle: "Vendor",
    columnChooserTitle: "Vendor",
    tooltip: "Vendor the row is sent to, taken from the purchase orders attached to it.",
    keyWords: ["vendor", "supplier", "manufacturer"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.DraftPurchaseOrder]: {
    id: BuySpecificColumn.DraftPurchaseOrder,
    columnTitle: "PO ID",
    columnChooserTitle: "PO ID",
    tooltip:
      "The id of the purchase order this row's quantity currently belongs to. Blank until the row has been turned into its own purchase order.",
    keyWords: ["po", "purchase order", "draft", "status", "split", "separated", "id"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.PurchaseOrderStatus]: {
    id: BuySpecificColumn.PurchaseOrderStatus,
    columnTitle: "PO Status",
    columnChooserTitle: "PO Status",
    tooltip:
      "The distinct purchase order statuses this row's quantity currently sits on. Blank until the row has been turned into its own purchase order.",
    keyWords: ["po", "purchase order", "status", "draft", "sent", "registered", "delivered"],
    group: TableColumnGroups.OrderQuantities,
  },
  [BuySpecificColumn.Notes]: {
    id: BuySpecificColumn.Notes,
    columnTitle: "Comments",
    columnChooserTitle: "Comments",
    tooltip: "Comments associated with the row.",
    group: TableColumnGroups.Actions,
  },
}

const PLAN_PERIOD_SCOPE = "Covers the season for fashion and the next 52 weeks for NOOS."

/**
 * Demand 2.0 Total tab columns. Keyed by the string colId (the `Demand2TotalColumn` enum values) for the
 * same reason the Performance set above is — `totalColumnIds` is imported here `import type` only.
 *
 * `columnSubtitle` is set only where the subtitle is a property of the column itself. Baseline, Manual,
 * Total demand and Forecasted pre-orders answer to the collection's planning window, so `totalColumns`
 * spreads theirs in per render; a registry entry would leak into the season case, which deliberately
 * carries a window tooltip and no subtitle. The column factories apply `tooltip` as `headerTooltip`,
 * and an explicit `headerTooltip` at the call site (the season window) still wins.
 */

const demand2TotalColumnNaming: Record<string, ColumnNaming> = {
  "demand2-lifecycle": {
    id: "demand2-lifecycle" as NamedColumnId,
    columnTitle: "Lifecycle",
    columnChooserTitle: "Lifecycle",
    tooltip:
      "Where the variant sits in its cycle. Hover for the dates, history and phase-out terms behind the state.",
    keyWords: [
      "lifecycle",
      "phase out",
      "phasing out",
      "phasing in",
      "new",
      "upcoming",
      "introduced",
      "status",
      "stage",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-variant-type": {
    id: "demand2-variant-type" as NamedColumnId,
    columnTitle: "Type",
    columnChooserTitle: "New vs carry-over",
    tooltip: `New variant, planned off comparable variants, or carry-over, planned off its own past ${formatName("sale", true)}.`,
    keyWords: [
      "npi",
      "new variant",
      "new product",
      "carry-over",
      "carryover",
      "type",
      "new vs carry over",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-own-insight-answers": {
    id: "demand2-own-insight-answers" as NamedColumnId,
    columnTitle: "Own insight answer",
    columnChooserTitle: "Own insight answer",
    tooltip:
      "Insights this variant answers on its own instead of following the collection. Reset them in Demand configuration.",
    keyWords: ["own answer", "insight", "override", "follows collection", "variant answer"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-review": {
    id: "demand2-review" as NamedColumnId,
    columnTitle: "Review",
    columnChooserTitle: "Review status",
    tooltip:
      "Whether this variant's demand has been reviewed before publishing. Groups show how many of their variants are reviewed.",
    keyWords: ["review", "reviewed", "pending", "status", "checked", "sign off"],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-baseline": {
    id: "demand2-baseline" as NamedColumnId,
    columnTitle: "Baseline",
    columnChooserTitle: "Baseline demand",
    tooltip: `Hakio's baseline demand in units before signals and overrides. ${PLAN_PERIOD_SCOPE}`,
    keyWords: ["baseline", "last year", "historic demand", "starting point"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-setup": {
    id: "demand2-setup" as NamedColumnId,
    columnTitle: "Demand signals",
    columnChooserTitle: "Demand signals",
    tooltip: `Change in units that demand signals add to the baseline. ${PLAN_PERIOD_SCOPE}`,
    keyWords: [
      "demand signals",
      "setup",
      "insights",
      "model insights",
      "strategic assumptions",
      // The pre-2026-09 name for this column. Kept so a planner who learned it still finds the column.
      "assumptions",
      "signals",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-signal-based": {
    id: "demand2-signal-based" as NamedColumnId,
    columnTitle: "Signal based demand",
    columnChooserTitle: "Signal based demand",
    tooltip: `Baseline plus demand signals in units, before manual forecasts and campaigns. ${PLAN_PERIOD_SCOPE}`,
    keyWords: [
      "signal based demand",
      "underlying demand",
      "setup forecast",
      "before manual",
      "demand signals total",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-forecast": {
    id: "demand2-forecast" as NamedColumnId,
    columnTitle: "Manual forecast",
    columnChooserTitle: "Manual forecast",
    tooltip: `Change in units a manual forecast makes to signal based demand. ${PLAN_PERIOD_SCOPE}`,
    keyWords: ["manual forecast", "manual", "override", "pin", "planned demand"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-adjustments": {
    id: "demand2-adjustments" as NamedColumnId,
    columnTitle: "Campaigns",
    columnChooserTitle: "Campaigns",
    tooltip: `Change in units from campaigns and booked ${formatName("preorder")} uplift on top of the manual forecast. ${PLAN_PERIOD_SCOPE}`,
    keyWords: ["campaigns", "adjustments", "on-top", "overlay", "uplift"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-total-demand": {
    id: "demand2-total-demand" as NamedColumnId,
    columnTitle: "Total demand",
    columnChooserTitle: "Total demand",
    tooltip: `Expected demand in units: forecast plus ${formatName("preorder", true)}. This is what publishing writes. ${PLAN_PERIOD_SCOPE}`,
    keyWords: ["total demand", "expected demand", "planned demand", "total", "demand"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-published": {
    id: "demand2-published" as NamedColumnId,
    columnTitle: "vs Published",
    columnChooserTitle: "vs Published",
    tooltip:
      "Draft total demand minus published demand, in units, over the weeks the active publish covers.",
    keyWords: ["published", "vs published", "drift", "unpublished changes", "publish"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-forecasted-preorders": {
    id: "demand2-forecasted-preorders" as NamedColumnId,
    columnTitle: `Forecasted ${formatName("preorder", true, true)}`,
    columnChooserTitle: `Forecasted ${formatName("preorder", true, true)}`,
    tooltip: `Units of ${formatName("preorder", true)} expected but not booked yet, over the plan period. Part of total demand.`,
    keyWords: [
      formatName("preorder", true),
      "forecasted pre-orders",
      "expected pre-orders",
      "unbooked",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-confirmed-preorders": {
    id: "demand2-confirmed-preorders" as NamedColumnId,
    columnTitle: `Confirmed ${formatName("preorder", true, true)}`,
    columnChooserTitle: `Confirmed ${formatName("preorder", true, true)}`,
    tooltip: `${formatName("preorder", true, true)} already booked for this variant. Total demand stays at or above them unless a manual forecast or adjustment pulls it below.`,
    keyWords: [
      formatName("preorder", true),
      "confirmed pre-orders",
      "booked pre-orders",
      "known pre-orders",
      "order book",
      "committed",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-reorder-confidence": {
    id: "demand2-reorder-confidence" as NamedColumnId,
    columnTitle: "Reorder confidence",
    columnChooserTitle: "Reorder confidence",
    tooltip: `How broadly customers back this variant's ${formatName("preorder", true)}, from 0% to 100%. Many customers with none dominating reads High. Hover a row for the customers behind the score.`,
    keyWords: [
      "reorder confidence",
      "reorder",
      "confidence",
      `${formatName("preorder")} breadth`,
      "accounts",
      "customers",
      "concentration",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-stock-buy": {
    id: "demand2-stock-buy" as NamedColumnId,
    columnTitle: "Stock buy",
    columnChooserTitle: "Stock buy decision",
    tooltip: `Where the variant sits on the buy grid: ${formatName("preorder", true)} above or below the collection median, against how broadly customers back them. Hover a row for what the cell means.`,
    keyWords: [
      "stock buy",
      "decision",
      "buy grid",
      "cover pre-orders",
      "risk buy",
      "pass",
      "cover",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-ly-rev": {
    id: "demand2-ly-rev" as NamedColumnId,
    columnTitle: "LY revenue",
    columnSubtitle: "Last 52w",
    columnChooserTitle: "LY revenue",
    tooltip:
      "Revenue over the last 52 full weeks before the current week, across all warehouses and channels.",
    keyWords: ["revenue", "last year revenue", "ly revenue", "turnover"],
    group: TableColumnGroups.SalesAndForecast,
  },
  "demand2-seasonality": {
    id: "demand2-seasonality" as NamedColumnId,
    columnTitle: "Seasonality",
    columnChooserTitle: "Seasonality source",
    tooltip:
      "Where the weekly demand shape comes from, such as variant history or category. It sets the shape, not the amount.",
    keyWords: ["seasonality", "curve", "shape", "seasonal profile"],
    group: TableColumnGroups.ForecastMetrics,
  },
  "demand2-size-confidence": {
    id: "demand2-size-confidence" as NamedColumnId,
    columnTitle: "Size split",
    columnChooserTitle: "Size split confidence",
    tooltip: `How reliable the size split is. Historic for variants with their own ${formatName("sale", true)}, otherwise high to very low.`,
    keyWords: ["size split", "size confidence", "sizes", "size curve"],
    group: TableColumnGroups.ForecastMetrics,
  },
  "demand2-demand-status": {
    id: "demand2-demand-status" as NamedColumnId,
    columnTitle: "Demand status",
    columnChooserTitle: "Demand status",
    tooltip:
      "Demand alerts and informational statuses in one column. Hover a row for the full explanation.",
    keyWords: ["demand status", "alerts", "warnings", "info", "health"],
    group: TableColumnGroups.ForecastMetrics,
  },
  // The header renders blank (an icon-only actions column, so `totalColumns` keeps its own
  // `headerName: ""`); `columnTitle` exists for the interface and is never read for this column.
  "demand2-comments": {
    id: "demand2-comments" as NamedColumnId,
    columnTitle: "Comments",
    columnChooserTitle: "Comments",
    tooltip: "Comments on this variant's plan.",
    keyWords: ["comments", "notes", "discussion"],
    group: TableColumnGroups.Actions,
  },
}

const VARIANT_SIZES_NAMING = {
  columnTitle: "Sizes",
  columnChooserTitle: "Sizes",
  tooltip: "The variant's sizes, in size order.",
  keyWords: ["sizes", "size range", "size curve"],
  group: TableColumnGroups.ProductInformation,
} satisfies Omit<ColumnNaming, "id">

const PREORDER_CUSTOMERS_NAMING = {
  columnTitle: "Customers",
  columnChooserTitle: `${formatName("preorder", true, true)} customers`,
  tooltip: `Number of wholesale customers with ${formatName("preorder", true)} for the variant in this collection.`,
  keyWords: ["customers", formatName("preorder", true), "accounts", "buyers"],
  group: TableColumnGroups.DemandMetrics,
} satisfies Omit<ColumnNaming, "id">

/**
 * The remaining Demand 2.0 grids — assortment, budget, pre-orders and carry-over. Keyed by string
 * colId for the same reason the two records above are. Actions columns are here for the chooser
 * only — `createActionsColumn` doesn't consult the registry, so those keep their own `headerName`.
 */
const demand2ViewColumnNaming: Record<string, ColumnNaming> = {
  // Assortment → New variants (NPI matching)
  "demand2-npi-sales-since-start": {
    id: "demand2-npi-sales-since-start" as NamedColumnId,
    columnTitle: formatName("sale", true, true),
    columnSubtitle: "Since activation",
    columnChooserTitle: `${formatName("sale", true, true)} (Since activation)`,
    tooltip: `Units sold since the variant was activated, or first sold when it has no activation date, up to the current week and at most 52 weeks: the ${formatName("sale", true)} its baseline reads. ${formatName("sale", true, true)} before activation are left out. Across all warehouses and channels.`,
    keyWords: ["sales since activation", "sales", "units sold", "activation", "new"],
    group: TableColumnGroups.SalesAndForecast,
  },
  "demand2-npi-own-sales-baseline": {
    id: "demand2-npi-own-sales-baseline" as NamedColumnId,
    columnTitle: "Own sales baseline",
    columnSubtitle: "Last 52 weeks, with unobserved demand",
    columnChooserTitle: "Own sales baseline",
    tooltip: `What the variant sold over the last 52 weeks, plus the estimated unobserved demand for the weeks it couldn't sell. Blank until the variant has sold for at least 13 weeks.`,
    keyWords: [
      "own sales baseline",
      "own sales",
      "unobserved demand",
      "missing sales",
      "imputation",
      "last 52 weeks",
      "new",
      "introduced",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-npi-matching-method": {
    id: "demand2-npi-matching-method" as NamedColumnId,
    columnTitle: "Matching method",
    columnChooserTitle: "Matching method",
    tooltip:
      "How the comparables were found: saved matches, the variant's own carry-over, or neighbours in the comparison collection or elsewhere. Suggested methods are unconfirmed until a match is saved.",
    keyWords: [
      "matching method",
      "baseline method",
      "tier",
      "carry-over",
      "custom matches",
      "neighbors",
      "comparison collection",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-npi-match-count": {
    id: "demand2-npi-match-count" as NamedColumnId,
    columnTitle: "Match count",
    columnChooserTitle: "Match count",
    tooltip:
      "How many comparables the Match column is built from. Saved matches where any exist, else the suggested ones.",
    keyWords: ["match count", "number of matches", "comparables", "matches"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-npi-match-similarity": {
    id: "demand2-npi-match-similarity" as NamedColumnId,
    columnTitle: "Average similarity",
    columnChooserTitle: "Average similarity",
    tooltip:
      "The mean similarity of the comparables the Match column is built from. The badge in that column is the best comparable alone.",
    keyWords: ["average similarity", "match quality", "similarity", "matches"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-npi-real-matches": {
    id: "demand2-npi-real-matches" as NamedColumnId,
    columnTitle: "Saved matches",
    columnChooserTitle: "Saved matches",
    tooltip: "Number of accepted matches and their average similarity.",
    keyWords: ["saved matches", "accepted matches", "comparables", "match count"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-npi-activation-date": {
    id: "demand2-npi-activation-date" as NamedColumnId,
    columnTitle: "Activation date",
    columnChooserTitle: "Activation date",
    tooltip:
      "The earliest activation date across the variant's SKUs. Hakio forecasts the variant only once it has one.",
    keyWords: ["activation date", "activated from", "launch date", "go live"],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-baseline-status": {
    id: "demand2-baseline-status" as NamedColumnId,
    columnTitle: "Source",
    columnChooserTitle: "Baseline source",
    tooltip: `What the baseline plans on: own ${formatName("sale", true)}, matches, or a manual baseline. No baseline: plans at 0 until matches are confirmed.`,
    keyWords: [
      "source",
      "baseline source",
      "status",
      "no baseline",
      "ready to normalize",
      "normalized",
      "own sales",
      "matches",
      "manual",
      "baseline",
      "new",
    ],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-baseline-per-year": {
    id: "demand2-baseline-per-year" as NamedColumnId,
    columnTitle: "Baseline",
    columnChooserTitle: "Baseline",
    tooltip: `Units the plan stands on, the same as the Inspector's Baseline card. Fashion: collection ${formatName("sale", true)}, of the matches for new variants and its own past collection for carry-over. NOOS: a year of weekly ${formatName("sale", true)}, of the matches while phasing in, the source chosen for new variants, and its own past ${formatName("sale", true)} once mature or phased out. Empty until matches are confirmed. The total sums the rows shown.`,
    keyWords: ["baseline", "units", "quantity", "implied", "matched sales", "own sales", "new"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-baseline-weeks-of-sales": {
    id: "demand2-baseline-weeks-of-sales" as NamedColumnId,
    columnTitle: `Weeks of ${formatName("sale", true)}`,
    columnChooserTitle: `Weeks of ${formatName("sale", true)}`,
    tooltip: `Weeks since the variant was activated, or first sold when it has no activation date, weeks with no ${formatName("sale", true)} included. Once it has ${formatName("sale", true)} its matches can be adjusted by how it sold.`,
    keyWords: ["weeks of sales", "weeks", "new"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-baseline-matches": {
    id: "demand2-baseline-matches" as NamedColumnId,
    columnTitle: "Matches",
    columnSubtitle: "Accepted, else suggested",
    columnChooserTitle: "Matches",
    tooltip:
      "The variant's matches: accepted ones if any, otherwise suggestions (muted). Shows the matches' average similarity and how many more there are. The products are in the Inspector.",
    keyWords: [
      "matches",
      "match",
      "comparable",
      "suggestion",
      "similarity",
      "lookalike",
      "npi",
      "new",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-baseline-action": {
    id: "demand2-baseline-action" as NamedColumnId,
    columnTitle: "Actions",
    columnChooserTitle: "Baseline action",
    keyWords: ["actions", "set baseline", "review", "new"],
    group: TableColumnGroups.Actions,
  },
  "demand2-npi-status": {
    id: "demand2-npi-status" as NamedColumnId,
    columnTitle: "Status",
    columnChooserTitle: "Match status",
    tooltip: `Where the variant is in matching: own ${formatName("sale", true)}, no comparable, unmatched, needs activation, or matched.`,
    keyWords: ["status", "matched", "unmatched", "no comparable", "activate"],
    group: TableColumnGroups.ProductInformation,
  },

  // Assortment → Carry-over
  "demand2-co-sizes": {
    id: "demand2-co-sizes" as NamedColumnId,
    ...VARIANT_SIZES_NAMING,
  },
  // Assortment → any NOOS stage: which weeks a baseline was read over (NOOS_PLAN_PERIOD.md rule 4)
  "demand2-pp-baseline-source": {
    id: "demand2-pp-baseline-source" as NamedColumnId,
    columnTitle: "Baseline source",
    columnChooserTitle: "Baseline source",
    tooltip: `What the baseline is read from: own ${formatName("sale", true)}, own ${formatName("sale", true)} plus unobserved demand, matches, sold plus matches, or a ${formatName("planner")}-set level.`,
    keyWords: ["baseline source", "own sales", "matches", "planner-set", "basis"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-lifecycle-stage": {
    id: "demand2-lifecycle-stage" as NamedColumnId,
    columnTitle: "Lifecycle stage",
    columnChooserTitle: "Lifecycle stage",
    tooltip:
      "Whether the variant phases out or ends inside the plan's 52 weeks: Ongoing, Phasing out or Phased out. Read from its phase-out dates and lifecycle status, not from when it started selling.",
    keyWords: ["lifecycle stage", "phasing out", "phased out", "ongoing", "lifecycle", "phase-out"],
    group: TableColumnGroups.ProductInformation,
  },
  "demand2-pp-active-weeks": {
    id: "demand2-pp-active-weeks" as NamedColumnId,
    columnTitle: "Weeks in planning horizon",
    columnSubtitle: "Of the plan's 52",
    columnChooserTitle: "Weeks in planning horizon",
    tooltip:
      "How many of the plan's 52 weeks the variant will sell in, with the start and end dates that limit them. Hover for every date behind the count. 0 once it is phased out. A phase-out date alone doesn't shorten it.",
    keyWords: [
      "weeks in planning horizon",
      "planning horizon",
      "remaining weeks of sale",
      "remaining weeks",
      "active weeks",
      "plan period",
      "weeks",
      "start date",
      "end date",
      "activated until",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-pp-source-window": {
    id: "demand2-pp-source-window" as NamedColumnId,
    columnTitle: "Weeks read",
    columnChooserTitle: "Weeks read",
    tooltip:
      "Which weeks of the source the baseline sums. As many as the variant is active, never more: a launch reads its matches' first weeks, an ending variant its last year less the weeks after its end.",
    keyWords: ["weeks read", "source window", "window", "first weeks", "last 52 weeks"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-pp-full-year-baseline": {
    id: "demand2-pp-full-year-baseline" as NamedColumnId,
    columnTitle: "Full-year baseline",
    columnSubtitle: "For comparison",
    columnChooserTitle: "Full-year baseline",
    tooltip:
      "The same source over a full 52 weeks. Blank when the variant is active the whole plan, since the baseline is then the full year.",
    keyWords: ["full year", "full-year baseline", "annual", "comparison", "52 weeks"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-co-weeks-since-activation": {
    id: "demand2-co-weeks-since-activation" as NamedColumnId,
    columnTitle: "Weeks since activation",
    columnChooserTitle: "Weeks since activation",
    tooltip:
      "Whole weeks since the week the variant was activated, or first sold when it has no activation date. The same start the lifecycle stages read. Reads 0 in that week.",
    keyWords: [
      "weeks since activation",
      "activated",
      "activation",
      "age",
      "weeks live",
      "first sale",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-co-phasing-out-from": {
    id: "demand2-co-phasing-out-from" as NamedColumnId,
    columnTitle: "Phasing out from",
    columnChooserTitle: "Phasing out from",
    tooltip: "The date the variant stops being bought and reads as Phasing out.",
    keyWords: ["phasing out from", "phase out date", "phasing out", "stop buying"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-co-phased-out-from": {
    id: "demand2-co-phased-out-from" as NamedColumnId,
    columnTitle: "Phased out from",
    columnChooserTitle: "Phased out from",
    tooltip:
      "The week the variant reads as Phased out: the week after its Activated until date. Blank when it has no end date.",
    keyWords: ["phased out from", "phased out", "end date", "activated until"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-co-matching-method": {
    id: "demand2-co-matching-method" as NamedColumnId,
    columnTitle: "Matching method",
    columnChooserTitle: "Matching method",
    tooltip:
      "Where the carried-over baseline comes from: the comparison collection (Carry-over) or another closed season (Carry-over from other collections). Custom matches when a match is saved.",
    keyWords: [
      "matching method",
      "baseline method",
      "carry-over",
      "other collection",
      "custom matches",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-co-ly-units": {
    id: "demand2-co-ly-units" as NamedColumnId,
    columnTitle: formatName("sale", true, true),
    columnSubtitle: "Last 52 weeks",
    columnChooserTitle: `${formatName("sale", true, true)} (Last 52 weeks)`,
    tooltip:
      "Units sold over the last 52 full weeks before the current week, for the selected warehouse and channel.",
    keyWords: ["ly units", "last year units", "historic sales", "units sold", "sales volume"],
    group: TableColumnGroups.SalesAndForecast,
  },
  "demand2-co-ly-rev": {
    id: "demand2-co-ly-rev" as NamedColumnId,
    columnTitle: "Revenue",
    columnSubtitle: "Last 52 weeks",
    columnChooserTitle: "Revenue (Last 52 weeks)",
    tooltip:
      "Revenue over the last 52 full weeks before the current week, for the selected warehouse and channel.",
    keyWords: ["ly revenue", "last year revenue", "turnover", "sales value"],
    group: TableColumnGroups.SalesAndForecast,
  },
  "demand2-co-stock": {
    id: "demand2-co-stock" as NamedColumnId,
    columnTitle: "Stock level",
    columnSubtitle: "Today",
    columnChooserTitle: "Stock level",
    tooltip:
      "Available stock in units today, excluding reserved stock, for the selected warehouse.",
    keyWords: ["stock", "inventory", "on hand", "stock level"],
    group: TableColumnGroups.AvailabilityAndInventory,
  },
  "demand2-co-po-customers": {
    id: "demand2-co-po-customers" as NamedColumnId,
    ...PREORDER_CUSTOMERS_NAMING,
  },
  "demand2-co-po-open": {
    id: "demand2-co-po-open" as NamedColumnId,
    columnTitle: `Open ${formatName("preorder", true)}`,
    columnChooserTitle: `Open ${formatName("preorder", true)}`,
    tooltip: `Units of open ${formatName("preorder", true)} with a delivery date after today, across all collections.`,
    keyWords: ["open", formatName("preorder", true), "committed", "booked"],
    group: TableColumnGroups.DemandMetrics,
  },

  // Demand → Pre-orders
  "demand2-po-customers": {
    id: "demand2-po-customers" as NamedColumnId,
    ...PREORDER_CUSTOMERS_NAMING,
  },
  "demand2-po-qty": {
    id: "demand2-po-qty" as NamedColumnId,
    columnTitle: `Total ${formatName("preorder", true)}`,
    columnChooserTitle: `Total ${formatName("preorder", true)}`,
    tooltip: `Wholesale ${formatName("preorder", true)} in units for the variant in this collection.`,
    keyWords: [
      "units",
      "quantity",
      formatName("preorder", true),
      `total ${formatName("preorder", true)}`,
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-largest-customer": {
    id: "demand2-po-largest-customer" as NamedColumnId,
    columnTitle: "Largest customer",
    columnSubtitle: `${formatName("preorder", false, true)} share`,
    columnChooserTitle: `Largest customer ${formatName("preorder", false)} share`,
    tooltip: `The largest single customer's share of this variant's ${formatName("preorder", true)}. When one customer holds most of them, they are a weaker signal for in-season reorders.`,
    keyWords: [
      "largest customer",
      "concentration",
      "top customer",
      "share",
      "customers",
      "accounts",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-avg": {
    id: "demand2-po-avg" as NamedColumnId,
    columnTitle: "Avg / customer",
    columnChooserTitle: `Average ${formatName("preorder", true)} per customer`,
    tooltip: `Average ${formatName("preorder")} units per customer for the variant in this collection.`,
    keyWords: ["average", "per customer", "avg", formatName("preorder", true)],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-share": {
    id: "demand2-po-share" as NamedColumnId,
    columnTitle: "% of LY demand",
    columnChooserTitle: `${formatName("preorder", true, true)} share of LY demand`,
    tooltip: `${formatName("preorder", true, true)} as a share of ${formatName("preorder", true)} plus ${formatName("sale", true)} over the last 52 full weeks.`,
    keyWords: ["share", "percent", "of ly demand", "coverage"],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-co-preorders": {
    id: "demand2-po-co-preorders" as NamedColumnId,
    columnTitle: formatName("preorder", true, true),
    columnChooserTitle: `Carry-over ${formatName("preorder", true)}`,
    tooltip: `Last season's wholesale ${formatName("preorder", true)}: the variant's own, or its accepted comparables' weighted by similarity. The same season the Total table's baseline reads.`,
    keyWords: [
      "carry-over",
      "carryover",
      "last season",
      "previous season",
      "ly pre-orders",
      "pre-orders",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-co-reorders": {
    id: "demand2-po-co-reorders" as NamedColumnId,
    columnTitle: "Reorders",
    columnChooserTitle: "Carry-over reorders",
    tooltip: `What wholesale customers bought in season last season, on top of their delivered ${formatName("preorder", true)}. Same season and comparables as the ${formatName("preorder", true)} beside it.`,
    keyWords: [
      "carry-over",
      "carryover",
      "last season",
      "previous season",
      "reorders",
      "in season",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
  "demand2-po-account-share": {
    id: "demand2-po-account-share" as NamedColumnId,
    columnTitle: "Share of customers",
    columnChooserTitle: "Share of customers",
    tooltip: `Customers that placed ${formatName("preorder", true)} for this variant, as a share of the customers active in its collections.`,
    keyWords: [
      "share of customers",
      "customer share",
      "account share",
      "breadth",
      "customers",
      "accounts",
      "reach",
    ],
    group: TableColumnGroups.DemandMetrics,
  },
}

/** NPI list (new product introduction overview) namings. */
export const npiListColumnNaming: Record<NpiListColumns, ColumnNaming> = {
  [NpiListColumns.FirstYearSales]: {
    id: NpiListColumns.FirstYearSales,
    columnTitle: "Demand",
    columnSubtitle: "Next 52 weeks",
    columnChooserTitle: "Demand (Next 52 weeks)",
    tooltip: "The expected demand quantity to sell during the period.",
    keyWords: ["demand", "forecast", "next 52 weeks", "expected sales", "npi forecast"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.EstimatedFirstYearSales]: {
    id: NpiListColumns.EstimatedFirstYearSales,
    columnTitle: `Estimated ${formatName("sale", true, true)}`,
    columnSubtitle: "First 52 weeks from matches",
    columnChooserTitle: `Estimated ${formatName("sale", true, true)} (First 52 weeks from matches)`,
    tooltip: `First-52-weeks ${formatName("sale", true)} are estimated by comparing the product to similar ones from the past. We look at how much those similar products sold on the selected demand basis, then predict future ${formatName("sale", true)} from that. The most similar matches carry more weight.`,
    keyWords: [
      "baseline",
      "estimated sales",
      "first 52 weeks",
      "match estimate",
      "userset value",
      "year 1",
      "npi",
    ],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.YearOneDemand]: {
    id: NpiListColumns.YearOneDemand,
    columnTitle: "Demand",
    columnSubtitle: "First 52 weeks",
    columnChooserTitle: "Demand (First 52 weeks)",
    tooltip:
      "The current demand over the variant's first 52 weeks of life. Differs from Demand (Next 52 weeks) once a variant has been live for a while: that window is the next 52 calendar weeks from today, this one is anchored to when the variant went live.",
    keyWords: ["demand", "first 52 weeks", "year 1", "life", "npi"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.ManualMatch]: {
    id: NpiListColumns.ManualMatch,
    columnTitle: "Forecasting method",
    columnChooserTitle: "Forecasting method",
    tooltip:
      "Whether the demand comes from the AI model or from manual matches. Adding matches only records them. Apply manual match switches the variant over to them, and Delete manual match hands it back to the AI model while keeping the matches. While applied, editing the matches updates the demand directly.",
    keyWords: [
      "forecasting method",
      "manual match",
      "ai forecast",
      "apply",
      "delete",
      "custom match",
      "npi",
    ],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.SimulatedDemand]: {
    id: NpiListColumns.SimulatedDemand,
    columnTitle: "Simulated demand",
    columnSubtitle: "First 52 weeks if applied",
    columnChooserTitle: "Simulated demand (First 52 weeks if applied)",
    tooltip: `What the demand would become if the estimated ${formatName("sale", true)} from the matches were applied. Can be altered by known demand, manual adjustments or if the product already is live.`,
    keyWords: [
      "simulated demand",
      "proposed demand",
      "new demand",
      "first 52 weeks",
      "match estimate",
      "npi",
    ],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.Info]: {
    id: NpiListColumns.Info,
    columnTitle: "Info",
    columnChooserTitle: "Info",
    tooltip: `Things that affect the variant's demand beyond its matches: locked adjustments overriding weeks, ${formatName("sale", true)} history of its own, and open ${formatName("preorder", true)}.`,
    keyWords: ["info", "locked adjustments", "historic sales", formatName("preorder", true), "npi"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.Update]: {
    id: NpiListColumns.Update,
    columnTitle: "Update NPI demand",
    columnChooserTitle: "Update NPI demand",
    tooltip: `Applied manual matches whose live estimate no longer agrees with the applied demand. The matches' ${formatName("sale", true)} data keeps changing while the applied demand stays a snapshot. The Update demand button re-applies the match with the current data.`,
    keyWords: ["update npi demand", "estimate changed", "drift", "reapply", "manual match", "npi"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.EstimatedSeasonality]: {
    id: NpiListColumns.EstimatedSeasonality,
    columnTitle: "Estimated Seasonality",
    columnChooserTitle: "Estimated Seasonality",
    tooltip:
      "The seasonality curve estimated for the variant, combining its intro curve and the calendar seasonality of its matches.",
    keyWords: ["seasonality", "curve", "intro", "calendar", "npi"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.MatchScore]: {
    id: NpiListColumns.MatchScore,
    columnTitle: "Match score",
    columnSubtitle: "Nearest neighbor similarity",
    columnChooserTitle: "Match score",
    tooltip:
      "Average similarity of the matches behind the variant's forecast. Custom (user-confirmed) matches show gray because the score is not an AI quality signal.",
    keyWords: ["match score", "similarity", "nearest neighbor", "ann", "match quality"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.MatchCount]: {
    id: NpiListColumns.MatchCount,
    columnTitle: "Match count",
    columnSubtitle: "Matches behind the forecast",
    columnChooserTitle: "Match count",
    tooltip:
      "Number of matches used for the variant's forecast: custom matches when they exist, otherwise the AI's nearest neighbors.",
    keyWords: ["match count", "matches", "number of matches", "nearest neighbors", "npi"],
    group: TableColumnGroups.NewProductIntroduction,
  },
  [NpiListColumns.WeeksSinceActivation]: {
    id: NpiListColumns.WeeksSinceActivation,
    columnTitle: "Weeks since activation",
    columnChooserTitle: "Weeks since activation",
    tooltip:
      "Whole ISO weeks between the week the variant was activated and the current week. Reads 0 during the activation week. Blank for variants that are not activated yet.",
    keyWords: [
      "weeks since activation",
      "activated",
      "activation",
      "age",
      "weeks live",
      "time on market",
      "npi",
    ],
    group: TableColumnGroups.NewProductIntroduction,
  },
}

const allocationStore = (
  id: AllocationStoreColumn,
  columnTitle: string,
  tooltip: string,
): ColumnNaming => ({
  id,
  columnTitle,
  columnChooserTitle: columnTitle,
  tooltip,
  group: TableColumnGroups.AllocationInfo,
})

/** Settings › Stores namings. */
const allocationStoreColumnNaming: Record<AllocationStoreColumn, ColumnNaming> = {
  [AllocationStoreColumn.Name]: allocationStore(
    AllocationStoreColumn.Name,
    "Store",
    "The store's name, from your store data",
  ),
  [AllocationStoreColumn.Code]: allocationStore(
    AllocationStoreColumn.Code,
    "Store ID",
    "The store's ID in your own systems. Read only",
  ),
  [AllocationStoreColumn.Grade]: allocationStore(
    AllocationStoreColumn.Grade,
    STORE_FIELD_LABELS.grade,
    "A, B, C or D. Drives plan targets and rule assignment. Outlets carry no grade",
  ),
  [AllocationStoreColumn.Format]: allocationStore(
    AllocationStoreColumn.Format,
    STORE_FIELD_LABELS.format,
    "Flagship, Outlet, Shop-in-shop, Partner or Pop-up",
  ),
  [AllocationStoreColumn.Size]: allocationStore(
    AllocationStoreColumn.Size,
    STORE_FIELD_LABELS.storeSize,
    "Small, Medium or Large. Set apart from the selling area, never worked out from it",
  ),
  [AllocationStoreColumn.SellingArea]: allocationStore(
    AllocationStoreColumn.SellingArea,
    STORE_FIELD_LABELS.sellingArea,
    "The store's selling floor in square metres",
  ),
  [AllocationStoreColumn.TransitTime]: allocationStore(
    AllocationStoreColumn.TransitTime,
    STORE_FIELD_LABELS.transitDays,
    "Days from dispatch at the warehouse to receipt at the store. Every replenishment rule reads it from here",
  ),
  [AllocationStoreColumn.DeliveryAddress]: allocationStore(
    AllocationStoreColumn.DeliveryAddress,
    STORE_FIELD_LABELS.deliveryAddress,
    "Where deliveries to the store go. Not the billing address",
  ),
  [AllocationStoreColumn.Country]: allocationStore(
    AllocationStoreColumn.Country,
    "Country",
    "From your store data. Read only",
  ),
  [AllocationStoreColumn.Rule]: allocationStore(
    AllocationStoreColumn.Rule,
    "Replenishment rule",
    "The store's default rule, set in Rules. A store and package pair can override it",
  ),
}

// "L7D sales (units)", "L7D variant sales (units)". Every sales horizon in allocation follows it.
export const salesColumnTitle = (window: string, prefix?: string) =>
  `${window} ${prefix ? `${prefix.toLowerCase()} ` : ""}${formatName("sale", true)} (units)`

const replenishment = (
  id: ReplenishmentTableColumn,
  columnTitle: string,
  group: TableColumnGroups,
  tooltip: string,
  columnChooserTitle = columnTitle,
  keyWords?: string[],
): ColumnNaming => ({ id, columnTitle, columnChooserTitle, tooltip, group, keyWords })

export const replenishmentColumnNaming: Record<ReplenishmentTableColumn, ColumnNaming> = {
  [ReplenishmentTableColumn.Warning]: replenishment(
    ReplenishmentTableColumn.Warning,
    "",
    TableColumnGroups.ProductInformation,
    "A warning icon on lines whose suggested qty rests on weak evidence; hover for the reasons. Shows only while a line in the table has a warning.",
    "Warnings",
  ),
  [ReplenishmentTableColumn.PackageName]: replenishment(
    ReplenishmentTableColumn.PackageName,
    "Product package",
    TableColumnGroups.ProductInformation,
    "The named product list, store list and validity window this line is eligible through. No active package for a store means no replenishment.",
  ),
  [ReplenishmentTableColumn.StoreId]: replenishment(
    ReplenishmentTableColumn.StoreId,
    "Store ID",
    TableColumnGroups.AllocationInfo,
    "The store's ID in your own systems",
  ),
  [ReplenishmentTableColumn.Block]: replenishment(
    ReplenishmentTableColumn.Block,
    "Block",
    TableColumnGroups.AllocationInfo,
    "The block the destination store belongs to.",
  ),
  [ReplenishmentTableColumn.ColorCode]: replenishment(
    ReplenishmentTableColumn.ColorCode,
    "Color code",
    TableColumnGroups.ProductInformation,
    "The part of the variant ID after the product ID",
  ),
  [ReplenishmentTableColumn.SkuId]: replenishment(
    ReplenishmentTableColumn.SkuId,
    "SKU ID",
    TableColumnGroups.ProductInformation,
    "The SKU's ID in your own systems: one size of one variant.",
  ),
  [ReplenishmentTableColumn.SourceLocation]: replenishment(
    ReplenishmentTableColumn.SourceLocation,
    "Source location",
    TableColumnGroups.AllocationInfo,
    "Ship-from node: the warehouse (WH) this line is sourced from.",
  ),
  [ReplenishmentTableColumn.DestinationLocation]: replenishment(
    ReplenishmentTableColumn.DestinationLocation,
    "Destination location",
    TableColumnGroups.AllocationInfo,
    "Ship-to node: the store receiving this line.",
  ),
  [ReplenishmentTableColumn.ChangesDueBy]: replenishment(
    ReplenishmentTableColumn.ChangesDueBy,
    "Changes due by",
    TableColumnGroups.AllocationInfo,
    "Finish changes to this line by this time to make the next departure. Later changes wait for the departure after it.",
  ),
  [ReplenishmentTableColumn.PlannedDeliveryDate]: replenishment(
    ReplenishmentTableColumn.PlannedDeliveryDate,
    "Planned delivery date",
    TableColumnGroups.AllocationInfo,
    "When the next departure reaches the store: the departure day plus the store's transit time.",
  ),
  [ReplenishmentTableColumn.InventoryPosition]: replenishment(
    ReplenishmentTableColumn.InventoryPosition,
    "Inventory position",
    TableColumnGroups.AvailabilityAndInventory,
    "Location stock on hand plus in transit (inbound). Compared against the reorder point and the overstock level.",
  ),
  [ReplenishmentTableColumn.DaysOfStock]: replenishment(
    ReplenishmentTableColumn.DaysOfStock,
    "Days of stock",
    TableColumnGroups.Suggestion,
    "Inventory position / ROS, both as the suggested qty run saw them: how many days that stock lasts at that rate. Forward-looking, unlike days w/ stock. Blank when the run had no ROS basis.",
  ),
  [ReplenishmentTableColumn.StockStatus]: replenishment(
    ReplenishmentTableColumn.StockStatus,
    "Stock status",
    TableColumnGroups.Suggestion,
    "Inventory position against the rule's levels: Below reorder point, In range, or Above overstock level. Excluded from replenishment when a store × SKU setting excludes the line.",
  ),
  [ReplenishmentTableColumn.SuggestedQuantity]: replenishment(
    ReplenishmentTableColumn.SuggestedQuantity,
    "Suggested qty",
    TableColumnGroups.Suggestion,
    "What the engine recommends, shown as SQ. Hover the number to see how it was derived.",
  ),
  [ReplenishmentTableColumn.UnrestrictedSuggestion]: replenishment(
    ReplenishmentTableColumn.UnrestrictedSuggestion,
    "Unrestricted suggestion",
    TableColumnGroups.Suggestion,
    "What the rule and the rate of sale ask for, before the minimum display quantity, maximum stock and order multiple: the order-up-to level minus the inventory position, or 0 above the reorder point.",
  ),
  [ReplenishmentTableColumn.MinDisplayQty]: replenishment(
    ReplenishmentTableColumn.MinDisplayQty,
    "Minimum display quantity",
    TableColumnGroups.ProductSettings,
    "The fewest units the store must show, as the run applied it: the store × SKU setting, else the variant setting. Blank when neither is set.",
  ),
  [ReplenishmentTableColumn.MaxQty]: replenishment(
    ReplenishmentTableColumn.MaxQty,
    "Maximum stock",
    TableColumnGroups.ProductSettings,
    "Inventory position plus suggested qty never goes above it. Blank when no setting caps the line.",
  ),
  [ReplenishmentTableColumn.OrderMultiple]: replenishment(
    ReplenishmentTableColumn.OrderMultiple,
    "Order multiple",
    TableColumnGroups.ProductSettings,
    "The suggested qty rounds up to a multiple of it, or down when rounding up would pass the maximum stock.",
  ),
  [ReplenishmentTableColumn.StoreSalesL7]: replenishment(
    ReplenishmentTableColumn.StoreSalesL7,
    salesColumnTitle("L7D"),
    TableColumnGroups.SalesAndForecast,
    "Units of this SKU sold at this store in the last 7 days.",
  ),
  [ReplenishmentTableColumn.StoreSalesL14]: replenishment(
    ReplenishmentTableColumn.StoreSalesL14,
    salesColumnTitle("L14D"),
    TableColumnGroups.SalesAndForecast,
    "Units of this SKU sold at this store in the last 14 days.",
  ),
  [ReplenishmentTableColumn.StoreSalesWindow]: replenishment(
    ReplenishmentTableColumn.StoreSalesWindow,
    salesColumnTitle("L28D"),
    TableColumnGroups.SalesAndForecast,
    "Units of this SKU sold at this store in the last 28 days.",
  ),
  [ReplenishmentTableColumn.RateOfSaleWindow]: replenishment(
    ReplenishmentTableColumn.RateOfSaleWindow,
    "ROS (sales history window)",
    TableColumnGroups.Suggestion,
    "Rate of sale (ROS) the suggested qty run used: units sold per day w/ stock, the three sales periods blended by the period weights, or the Reference ROS when the SKU is below the minimum days w/ stock. Hover the number for the calculation. Blank when the run had no ROS basis. Group rows sum their lines.",
  ),
  [ReplenishmentTableColumn.ExpectedSalesNext7Days]: replenishment(
    ReplenishmentTableColumn.ExpectedSalesNext7Days,
    salesColumnTitle("Next 7D"),
    TableColumnGroups.Suggestion,
    "Units this store sells in the coming week at the ROS the suggested qty run used: ROS × 7. Not a forecast: no seasonality or campaigns. Blank when the run had no ROS basis. Group rows sum their lines.",
    undefined,
    ["expected sales", "next 7 days"],
  ),
  [ReplenishmentTableColumn.RosBasis]: replenishment(
    ReplenishmentTableColumn.RosBasis,
    "ROS basis",
    TableColumnGroups.Suggestion,
    "Where the ROS the suggested qty run used came from: Own ROS, or Reference ROS for a SKU below the minimum days w/ stock. No ROS basis when no Reference ROS level had enough lines. Blank on lines from runs before the basis was stored.",
  ),
  [ReplenishmentTableColumn.RosFallbackLevel]: replenishment(
    ReplenishmentTableColumn.RosFallbackLevel,
    "ROS fallback level",
    TableColumnGroups.Suggestion,
    "The Reference ROS level the run took the average of, as its position in the chain (Level 1 is tried first). Hover for the level in words. Blank for lines on their own ROS and for runs that didn't store the level.",
  ),
  [ReplenishmentTableColumn.DaysWithInventory]: replenishment(
    ReplenishmentTableColumn.DaysWithInventory,
    "Days w/ stock",
    TableColumnGroups.SalesAndForecast,
    "Number of days in the ROS applied sales history window where the SKU was in stock in this location. Abbreviated as DWS.",
  ),
  [ReplenishmentTableColumn.VariantSalesL7]: replenishment(
    ReplenishmentTableColumn.VariantSalesL7,
    salesColumnTitle("L7D", "Variant"),
    TableColumnGroups.SalesAndForecast,
    "Units of every size of this variant sold at this store in the last 7 days.",
  ),
  [ReplenishmentTableColumn.VariantRateOfSale]: replenishment(
    ReplenishmentTableColumn.VariantRateOfSale,
    "Variant ROS",
    TableColumnGroups.SalesAndForecast,
    "Rate of sale (ROS) of the whole variant at this store: the sum of its sizes' ROS today. Sizes with no ROS basis count as 0.",
  ),
  [ReplenishmentTableColumn.SkuShareOfVariant]: replenishment(
    ReplenishmentTableColumn.SkuShareOfVariant,
    "SKU share of variant",
    TableColumnGroups.SalesAndForecast,
    "This size's share of the variant's units sold at this store over the sales history window.",
  ),
  [ReplenishmentTableColumn.LocationStockOnHand]: replenishment(
    ReplenishmentTableColumn.LocationStockOnHand,
    "Location stock on hand",
    TableColumnGroups.AvailabilityAndInventory,
    "Physical sellable units at the destination. Excludes in transit.",
  ),
  [ReplenishmentTableColumn.InTransit]: replenishment(
    ReplenishmentTableColumn.InTransit,
    "In transit (inbound)",
    TableColumnGroups.AvailabilityAndInventory,
    "Units on open and shipped STOs the ERP reports to this store, not yet received.",
  ),
  [ReplenishmentTableColumn.WarehouseAvailable]: replenishment(
    ReplenishmentTableColumn.WarehouseAvailable,
    "WH available to allocate",
    TableColumnGroups.AvailabilityAndInventory,
    "Warehouse units free to be allocated to this location. Abbreviated as ATA (available to allocate).",
  ),
  [ReplenishmentTableColumn.WarehouseFreeStock]: replenishment(
    ReplenishmentTableColumn.WarehouseFreeStock,
    "Free stock",
    TableColumnGroups.AvailabilityAndInventory,
    "Free stock your ERP reports at this warehouse, before Hakio nets anything off.",
  ),
  [ReplenishmentTableColumn.WarehouseEndBalance]: replenishment(
    ReplenishmentTableColumn.WarehouseEndBalance,
    "End balance",
    TableColumnGroups.AvailabilityAndInventory,
    "Free stock at this warehouse plus the stock pools its end balance counts on top, such as the webshop pool.",
  ),
  [ReplenishmentTableColumn.BlockBalance]: replenishment(
    ReplenishmentTableColumn.BlockBalance,
    "Block balance",
    TableColumnGroups.AvailabilityAndInventory,
    "This SKU's stock on hand across the stores in this block, from the latest store stock date.",
  ),
  [ReplenishmentTableColumn.NetworkStock]: replenishment(
    ReplenishmentTableColumn.NetworkStock,
    "Network stock",
    TableColumnGroups.AvailabilityAndInventory,
    "Every unit of this SKU across all warehouses, all stores and in transit (not including inbound to warehouses).",
  ),
  [ReplenishmentTableColumn.ValueAtRetail]: replenishment(
    ReplenishmentTableColumn.ValueAtRetail,
    "Value at retail",
    TableColumnGroups.Value,
    "Final qty × recommended retail price from the product master data. No VAT or markdown logic.",
  ),
  [ReplenishmentTableColumn.ValueAtCost]: replenishment(
    ReplenishmentTableColumn.ValueAtCost,
    "Value at cost",
    TableColumnGroups.Value,
    "Final qty × cost price from the product master data.",
  ),
  [ReplenishmentTableColumn.OverrideQuantity]: replenishment(
    ReplenishmentTableColumn.OverrideQuantity,
    "Override qty",
    TableColumnGroups.OrderQuantities,
    "Your quantity, replacing the suggestion. Maths works too: 5+50%, 5*1.5, +10 or -20% of the suggestion. Commits on Enter or when you click away.",
  ),
  [ReplenishmentTableColumn.FinalQuantity]: replenishment(
    ReplenishmentTableColumn.FinalQuantity,
    "Final qty",
    TableColumnGroups.OrderQuantities,
    "What ships if you confirm now: your override if set, otherwise the suggestion, zero if the line is excluded.",
  ),
  [ReplenishmentTableColumn.Included]: replenishment(
    ReplenishmentTableColumn.Included,
    "",
    TableColumnGroups.OrderQuantities,
    "Switch off to exclude this line. Excluded lines dim and count as zero.",
    "Include",
  ),
}

// Published-leaf table columns. String ids, not the enum: this file only imports it as a type, and a
// value import would pull the tab component in.
const leafNaming = (
  id: string,
  columnTitle: string,
  group: TableColumnGroups,
  extra: Partial<ColumnNaming> = {},
): ColumnNaming => ({
  id: id as NamedColumnId,
  columnTitle,
  columnChooserTitle: extra.columnChooserTitle ?? columnTitle,
  group,
  ...extra,
})
const demand2PublishedLeafColumnNaming: Record<string, ColumnNaming> = Object.fromEntries(
  [
    leafNaming("demand2-leaf-collection", "Collection", TableColumnGroups.Collections, {
      tooltip: "The collection the published demand row belongs to.",
    }),
    leafNaming("demand2-leaf-collection-id", "Collection ID", TableColumnGroups.Collections, {
      tooltip: "ID of the collection the published demand row belongs to.",
    }),
    leafNaming("demand2-leaf-workspace-id", "Workspace ID", TableColumnGroups.Collections, {
      tooltip: "The demand workspace the published row belongs to.",
    }),
    leafNaming("demand2-leaf-product", "Product", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.ProductName].tooltip,
    }),
    leafNaming("demand2-leaf-variant", "Variant", TableColumnGroups.ProductInformation, {
      tooltip: "The variant: usually one color of a product, with one SKU per size.",
    }),
    leafNaming("demand2-leaf-variant-id", "Variant ID", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.VariantId].tooltip,
    }),
    leafNaming("demand2-leaf-brand", "Brand", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.Brand].tooltip,
    }),
    leafNaming("demand2-leaf-category", "Category", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.Category].tooltip,
    }),
    leafNaming("demand2-leaf-gender", "Gender", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.Gender].tooltip,
    }),
    leafNaming("demand2-leaf-color", "Color", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.Color].tooltip,
    }),
    leafNaming("demand2-leaf-vendor", "Vendor", TableColumnGroups.ProductInformation, {
      tooltip: productColumnNaming[ProductTableColumn.Vendor].tooltip,
    }),
    leafNaming(
      "demand2-leaf-preferred-vendor",
      "Preferred vendor",
      TableColumnGroups.ProductInformation,
      { tooltip: productColumnNaming[ProductTableColumn.PreferredVendor].tooltip },
    ),
    leafNaming("demand2-leaf-product-type", "Product type", TableColumnGroups.ProductInformation, {
      tooltip: "NOOS or Fashion, from the variant's product settings.",
    }),
    leafNaming("demand2-leaf-abc", "ABC", TableColumnGroups.ProductInformation, {
      tooltip:
        "ABC class by revenue over the company's ABC period, 365 days by default. A is the top sellers, C the tail.",
    }),
    leafNaming("demand2-leaf-lead-time", "Total lead time", TableColumnGroups.OperationalMetrics, {
      tooltip:
        "Days from placing an order to stock arriving, as set for the variant. Uses the longest across its SKUs.",
    }),
    leafNaming(
      "demand2-leaf-purchase-interval",
      "Purchase interval",
      TableColumnGroups.OperationalMetrics,
      {
        tooltip:
          "Days between purchase orders, as set for the variant. Uses the longest across its SKUs.",
      },
    ),
    leafNaming("demand2-leaf-warehouse-id", "Warehouse", TableColumnGroups.DemandMetrics, {
      tooltip: "The warehouse the published demand row is for.",
    }),
    leafNaming("demand2-leaf-channel", "Channel", TableColumnGroups.DemandMetrics, {
      tooltip: "The channel the published demand row is for.",
    }),
    leafNaming("demand2-leaf-market-id", "Market ID", TableColumnGroups.DemandMetrics, {
      tooltip: "The market the published demand row is for.",
    }),
    leafNaming("demand2-leaf-date", "Published weeks", TableColumnGroups.DemandMetrics, {
      tooltip: "The first and last published week across the row's cells.",
    }),
    leafNaming("demand2-leaf-value", "Published demand", TableColumnGroups.DemandMetrics, {
      columnSubtitle: "Whole plan",
      columnChooserTitle: "Published demand (whole plan)",
      tooltip: `Published demand units across all plan weeks. Excludes ${formatName("preorder", true)}, shown in ${formatName("preorder", false, true)}.`,
    }),
    leafNaming(
      "demand2-leaf-preorder",
      formatName("preorder", false, true),
      TableColumnGroups.DemandMetrics,
      {
        columnSubtitle: "Whole plan",
        columnChooserTitle: `Published ${formatName("preorder")} (whole plan)`,
        tooltip: `${formatName("preorder", false, true)} units in the published plan, across all its weeks. On top of Published demand.`,
      },
    ),
    leafNaming("demand2-leaf-publish-id", "Publish ID", TableColumnGroups.DemandMetrics, {
      tooltip: "The publish that last wrote the row.",
    }),
    leafNaming(
      "demand2-leaf-stock-level",
      "Stock level",
      TableColumnGroups.AvailabilityAndInventory,
      {
        columnSubtitle: "Current",
        columnChooserTitle: "Stock level (current)",
        tooltip:
          "Stock on hand today that is not reserved. Grouped by warehouse, only that warehouse's stock.",
      },
    ),
    leafNaming(
      "demand2-leaf-weeks-on-hand",
      "Weeks on hand",
      TableColumnGroups.AvailabilityAndInventory,
      {
        columnSubtitle: "Current",
        columnChooserTitle: "Weeks on hand (current)",
        tooltip: `How many weeks today's stock lasts against expected ${formatName("sale", true)} demand.`,
      },
    ),
    leafNaming(
      "demand2-leaf-incoming",
      "Incoming stock",
      TableColumnGroups.AvailabilityAndInventory,
      {
        columnSubtitle: "Until end of collection",
        columnChooserTitle: "Incoming stock (until end of collection)",
        tooltip:
          "Purchase orders for the collection that have not arrived yet and are due before it ends. Fashion only.",
      },
    ),
    leafNaming(
      "demand2-leaf-end-stock",
      "Stock level",
      TableColumnGroups.AvailabilityAndInventory,
      {
        columnSubtitle: "End of period",
        columnChooserTitle: "Stock level (end of period)",
        tooltip: `Forecasted closing stock at the end of the collection (fashion) or the year (NOOS). For fashion it is stock level plus incoming stock, minus the published demand and ${formatName("preorder", true)} still to come.`,
      },
    ),
    leafNaming("demand2-leaf-forecast", "Forecast", TableColumnGroups.SalesPerformance, {
      columnSubtitle: "Forecast-covered, all time",
      columnChooserTitle: "Forecast (all time)",
      tooltip: "Expected demand summed over every forecast-covered week in the two-year lookback.",
    }),
    leafNaming("demand2-leaf-actual", "Actual", TableColumnGroups.SalesPerformance, {
      columnSubtitle: "Forecast-covered, all time",
      columnChooserTitle: "Actual (all time)",
      tooltip: `${formatName("sale", true, true)} in the forecast-covered weeks of the two-year lookback, for the row's own channel, warehouse and market.`,
    }),
    leafNaming("demand2-leaf-revenue-all", "Revenue", TableColumnGroups.SalesPerformance, {
      columnSubtitle: "Forecast-covered, all time",
      columnChooserTitle: "Revenue (all time)",
      tooltip: "Revenue in the forecast-covered weeks of the two-year lookback, net of returns.",
    }),
    leafNaming("demand2-leaf-weeks", "Weeks", TableColumnGroups.SalesPerformance, {
      columnSubtitle: "Forecast-covered, all time",
      columnChooserTitle: "Weeks (all time)",
      tooltip: `Weeks in the lookback with both a forecast and ${formatName("sale", true)}.`,
    }),
    leafNaming("demand2-leaf-sell-through", "Sell-through", TableColumnGroups.SalesPerformance, {
      columnSubtitle: "All time",
      columnChooserTitle: "Sell-through (all time)",
      tooltip: `${formatName("sale", true, true)} as a percentage of expected demand over the whole lookback.`,
    }),
    // One entry for every DEMAND2_PERF_WINDOWS window — each instance sets its own headerName/
    // headerSubtitle with the specific week count (`Performance 4w`, "Last 4 weeks", …), the same way
    // the period-chip columns above do; this covers the chooser label and the primary window's tooltip.
    leafNaming("demand2-leaf-perf", "Performance", TableColumnGroups.SalesPerformance, {
      tooltip: `${formatName("sale", true, true)} as a percentage of expected demand over the trailing window.`,
    }),
    leafNaming(
      "demand2-leaf-sales-vs-ly",
      `${formatName("sale", true, true)} vs LY`,
      TableColumnGroups.SalesPerformance,
      {
        columnSubtitle: "Last 12 weeks",
        columnChooserTitle: `${formatName("sale", true, true)} vs last year`,
        tooltip: `Last 12 weeks of ${formatName("sale", true)} against the same weeks a year earlier, per variant.`,
      },
    ),
    leafNaming("demand2-leaf-rules", "Rules", TableColumnGroups.SalesPerformance, {
      tooltip: "The rules this variant matches. Rules are set up in Settings › Rules.",
    }),
    leafNaming("demand2-leaf-status", "Status", TableColumnGroups.SalesPerformance, {
      tooltip: `Realised ${formatName("sale", true)} against published demand in the primary period, over the weeks that have both.`,
    }),
    leafNaming(
      "demand2-leaf-sales",
      formatName("sale", true, true),
      TableColumnGroups.SalesPerformance,
      {
        columnChooserTitle: `${formatName("sale", true, true)} (selected period)`,
        tooltip:
          "Units sold within the column's period, for the row's own channel, warehouse and market.",
      },
    ),
    leafNaming(
      "demand2-leaf-covered-sales",
      `${formatName("sale", true, true)} (forecast-covered)`,
      TableColumnGroups.SalesPerformance,
      {
        columnChooserTitle: `${formatName("sale", true, true)}, forecast-covered weeks (selected period)`,
        tooltip: `Units sold in the weeks a forecast covered. The ${formatName("sale", true)} that Status and ${formatName("sale", true)} vs expected are measured against.`,
      },
    ),
    leafNaming(
      "demand2-leaf-net-sales",
      `Net ${formatName("sale", true)}`,
      TableColumnGroups.SalesPerformance,
      {
        columnChooserTitle: `Net ${formatName("sale", true)} (selected period)`,
        tooltip: `Units sold minus units returned within the column's period. Returns are recorded when they are processed, so a short period can include returns from ${formatName("sale", true)} made before it.`,
      },
    ),
    leafNaming("demand2-leaf-returns", "Returns", TableColumnGroups.SalesPerformance, {
      columnChooserTitle: "Returns (selected period)",
      tooltip: "Units returned within the column's period, dated by when the return was processed.",
    }),
    leafNaming("demand2-leaf-revenue", "Revenue", TableColumnGroups.SalesPerformance, {
      columnChooserTitle: "Revenue (selected period)",
      tooltip: "Revenue within the column's period, net of returns.",
    }),
    leafNaming("demand2-leaf-asp", "ASP", TableColumnGroups.SalesPerformance, {
      columnChooserTitle: "ASP (selected period)",
      tooltip: "Average selling price: revenue divided by net units within the column's period.",
    }),
    leafNaming("demand2-leaf-cogs", "COGS", TableColumnGroups.SalesPerformance, {
      columnChooserTitle: "COGS (selected period)",
      tooltip: "Units sold times the current cost price. Missing cost prices count as 0.",
    }),
    leafNaming(
      "demand2-leaf-vs-expected",
      `${formatName("sale", true, true)} vs expected`,
      TableColumnGroups.SalesPerformance,
      {
        columnChooserTitle: `${formatName("sale", true, true)} vs expected (selected period)`,
        tooltip: `Realised ${formatName("sale", true)} as a percentage of expected demand over the weeks a forecast covered.`,
      },
    ),
  ].map((n) => [n.id, n]),
)

/**
 * The Demand 2.0 Total tab's per-signal columns: one per model insight, named by the label the insight
 * carries everywhere else (`insightLabelFor`), plus the three sums the inspector's How much card states.
 * Keyed by colId like the Total set above. Every one sits under Demand Metrics beside Demand signals, the
 * column they each isolate one term of.
 */
const demand2SignalColumnNaming: Record<string, ColumnNaming> = Object.fromEntries(
  [
    {
      id: Demand2SignalColumn.ModelInsights as NamedColumnId,
      columnTitle: "Model insights",
      columnChooserTitle: "Model insights",
      tooltip:
        "What the model's insights add to the variant's demand in total. The Model insights half of Demand signals.",
      keyWords: ["model insights", "insights", "demand signal", "signals"],
      group: TableColumnGroups.DemandMetrics,
    },
    {
      id: Demand2SignalColumn.Strategic as NamedColumnId,
      columnTitle: "Strategy",
      columnChooserTitle: "Strategy",
      tooltip:
        "What Strategy adds to the variant's demand: the Strategic assumptions set in Demand configuration plus your own Strategic adjustments. The Strategy half of Demand signals.",
      keyWords: [
        "strategy",
        "strategic assumptions",
        "strategic adjustments",
        "assumptions",
        "demand signal",
        "setup adjustments",
      ],
      group: TableColumnGroups.DemandMetrics,
    },
    {
      id: Demand2SignalColumn.Cannibalization as NamedColumnId,
      columnTitle: "Cannibalization",
      columnChooserTitle: "Cannibalization signal",
      tooltip:
        "What the Product mix answer in Demand configuration adds to or takes from the variant's demand.",
      keyWords: ["cannibalization", "product mix", "strategy", "demand signal"],
      group: TableColumnGroups.DemandMetrics,
    },
    ...INSIGHT_KEY_ORDER.map((key) => {
      const label = insightLabelFor(key)
      // The comparable season's growth is the planner's Strategy answer, so it reads with Strategy
      // rather than the Model insights.
      const strategic = isStrategicInsightKey(key)
      return {
        id: signalColumnId(key) as NamedColumnId,
        columnTitle: label,
        columnChooserTitle: label,
        tooltip: `What "${label}" adds to the variant's demand, in units. One of the ${strategic ? "Strategy" : "Model insights"} rows inside Demand signals; 0 where it does not apply.`,
        keyWords: [
          "demand signal",
          strategic ? "strategic assumption" : "model insight",
          "insight",
          label.toLowerCase(),
          key,
        ],
        group: TableColumnGroups.DemandMetrics,
      }
    }),
  ].map((n) => [n.id, n]),
)

/**
 * Every per-table naming record merged into one lookup. A later record replaces an earlier one's
 * entry for the same id string, so a record that shares an id must reuse the other record's entry
 * rather than copy it (checked in columnNaming.test.ts).
 */
export const columnNaming: Record<NamedColumnId, ColumnNaming> = {
  ...productColumnNaming,
  ...demand2PublishedLeafColumnNaming,
  ...demand2TotalColumnNaming,
  ...demand2SignalColumnNaming,
  ...demand2ViewColumnNaming,
  ...buyColumnNaming,
  ...collectionColumnNaming,
  ...supplyColumnNaming,
  ...noosOrderSuggestionsColumnNaming,
  ...salesMetricColumnNaming,
  ...trimMetricColumnNaming,
  ...budgetVariantColumnNaming,
  ...adjustmentManagerColumnNaming,
  ...npiDialogMatchColumnNaming,
  ...demandNpiMatchColumnNaming,
  ...marketStrategyColumnNaming,
  ...forecastEvaluationColumnNaming,
  ...purchaseOrderColumnNaming,
  ...npiListColumnNaming,
  ...replenishmentColumnNaming,
  ...allocationStoreColumnNaming,
}

/** The records merged into {@link columnNaming}, in the same order (checked in columnNaming.test.ts). */
export const columnNamingRecords: readonly Record<string, ColumnNaming>[] = [
  productColumnNaming,
  demand2PublishedLeafColumnNaming,
  demand2TotalColumnNaming,
  demand2SignalColumnNaming,
  demand2ViewColumnNaming,
  buyColumnNaming,
  collectionColumnNaming,
  supplyColumnNaming,
  noosOrderSuggestionsColumnNaming,
  salesMetricColumnNaming,
  trimMetricColumnNaming,
  budgetVariantColumnNaming,
  adjustmentManagerColumnNaming,
  npiDialogMatchColumnNaming,
  demandNpiMatchColumnNaming,
  marketStrategyColumnNaming,
  forecastEvaluationColumnNaming,
  purchaseOrderColumnNaming,
  npiListColumnNaming,
  replenishmentColumnNaming,
  allocationStoreColumnNaming,
]

export const columnIdsInGroups = (groups: readonly TableColumnGroups[]): NamedColumnId[] =>
  Object.values(columnNaming)
    .filter((naming) => naming.group !== undefined && groups.includes(naming.group))
    .map((naming) => naming.id)

/**
 * Naming for any column id, or `undefined` for ids that aren't in the registry (e.g. ad-hoc
 * list/detail columns). The shared column factories ({@link import("~/utils/columnTemplates")})
 * call this off `colId` to auto-populate `headerName`/`headerSubtitle`, so a column definition
 * usually only needs to pass `colId` — an explicit `headerName`/`headerSubtitle` at the call
 * site still wins.
 *
 * `tooltip` is applied as `headerTooltip` the same way, so it doubles as the header tooltip and the
 * column-chooser description.
 */
export const getColumnNaming = (id: string | undefined): ColumnNaming | undefined =>
  id ? (columnNaming as Record<string, ColumnNaming | undefined>)[id] : undefined
